import type { GridApi, IRowNode } from "ag-grid-community";
import { action, computed, makeAutoObservable, observable, ObservableMap } from "mobx";

import type {
  GridResponse,
  GridRow,
  JsonValue,
  SmartCell,
  SmartColumn,
  TruncatedValueStub,
} from "@/queries/smart-tables/types";
import { CellStatus, ColumnType, PENDING_CELL_STATUSES } from "@/queries/smart-tables/types";
import {
  isOutputMediaPayload,
  isOutputMediaPayloadArray,
  resolveCellDisplayState,
} from "@/routes/smart-table/utils/cellDisplayState";
import {
  isPlainTextEditableColumnType,
  isReadonlyAnchorColumnType,
  isRunnableColumnType,
  isStableDisplayColumnType,
} from "@/routes/smart-table/utils/columnTypeGuards";
import { isMultiContentValue } from "@/routes/smart-table/utils/multiContent";
import {
  assignCompositionSourceFields,
  normalizeCompositionDisplayValue,
  resolveCompositionDisplayValue,
} from "@/routes/smart-table/utils/compositionColumns";
import { buildSmartTableBaseColumnDefs } from "@/routes/smart-table/utils/smartTableBaseColumnDefs";
import {
  formatExecutionMetadataLatency,
  formatExecutionMetadataPrice,
  formatExecutionMetadataStructuredValue,
} from "@/routes/smart-table/utils/executionMetadataFormatting";
import {
  isExecutionMetadataChildColumn,
  isTopLevelGroupedChildColumn,
  nestSmartTableGroupedChildColumns,
  resolveGroupedChildIdsForParent,
  supportsExecutionMetadataColumns,
} from "@/routes/smart-table/utils/smartTableGroupedColumns";
import { isSmartTableColumnGroupDef } from "@/routes/smart-table/utils/smartTableColumnGroups";
import { traceSmartTable, traceSmartTableRows } from "@/routes/smart-table/utils/smartTableTrace";

// ────────────────────────────────────────────
// Snapshot types for rollback
// ────────────────────────────────────────────

export interface CellSnapshot {
  cellId: string;
  cell: SmartCell | null;
  rowIndex: number;
  columnId: string;
}

export interface ColumnSnapshot {
  columnId: string;
  column: SmartColumn | null;
  position: number;
  cells?: Record<number, SmartCell>;
}

interface PendingCreatedColumn {
  tempId: string;
  title: string;
  positionRank: number;
  column: SmartColumn;
  sourceColumnId?: string;
}

type JsonObject = Record<string, JsonValue>;

export interface OptimisticDuplicateColumnSpec {
  column: SmartColumn;
  sourceColumnId: string;
}

export interface ColumnReconciliationSpec {
  column: SmartColumn;
  tempId?: string;
}

interface AgGridValueFormatterParams {
  value?: unknown;
}
type AgGridValueFormatter = (params: AgGridValueFormatterParams) => string;

const isObjectValue = (value: SmartCell["value"]): value is JsonObject =>
  typeof value === "object" && value !== null && !Array.isArray(value);

// Cell values are replaced immutably in this store, so optimistic duplicate
// columns can share dense payload references until the new cells recompute.
const copyCellValueForColumnDuplicate = (value: SmartCell["value"]): SmartCell["value"] => value;

const DEFAULT_ROW_PAYLOAD_RETENTION_BLOCKS = 24;
const DEFAULT_ROW_PAYLOAD_RETENTION_BLOCK_SIZE = 25;

const getSmartTableRowPayloadRetentionBlocks = (): number => {
  if (typeof window === "undefined") {
    return DEFAULT_ROW_PAYLOAD_RETENTION_BLOCKS;
  }

  const override = window.__SMART_TABLE_PERF_TUNING__?.rowPayloadRetentionBlocks;
  if (typeof override !== "number" || !Number.isFinite(override)) {
    return DEFAULT_ROW_PAYLOAD_RETENTION_BLOCKS;
  }

  return Math.max(1, Math.min(200, Math.round(override)));
};

const getTruncatedValueSize = (value: SmartCell["value"]): number => {
  if (typeof value === "string") {
    return value.length;
  }

  try {
    return JSON.stringify(value)?.length ?? 0;
  } catch {
    return 0;
  }
};

const getTruncatedValueType = (value: SmartCell["value"]): string => {
  if (Array.isArray(value)) {
    return "array";
  }

  if (value === null) {
    return "null";
  }

  return typeof value;
};

const getTruncatedValueReferences = (
  value: SmartCell["value"],
): Pick<TruncatedValueStub, "request_id" | "trace_id"> => {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return {};
  }

  const root = value as Record<string, unknown>;
  const nestedValue =
    root.value && typeof root.value === "object" && !Array.isArray(root.value)
      ? (root.value as Record<string, unknown>)
      : undefined;
  const candidates = nestedValue ? [root, nestedValue] : [root];
  let requestId: string | number | undefined;
  let traceId: string | undefined;

  for (const candidate of candidates) {
    const candidateRequestId = candidate.request_id;
    if (
      requestId === undefined &&
      (typeof candidateRequestId === "string" || typeof candidateRequestId === "number")
    ) {
      requestId = candidateRequestId;
    }

    const candidateTraceId = candidate.trace_id;
    if (traceId === undefined && typeof candidateTraceId === "string" && candidateTraceId) {
      traceId = candidateTraceId;
    }

    const trace =
      candidate.trace && typeof candidate.trace === "object" && !Array.isArray(candidate.trace)
        ? (candidate.trace as Record<string, unknown>)
        : undefined;
    if (traceId === undefined && typeof trace?.trace_id === "string" && trace.trace_id) {
      traceId = trace.trace_id;
    }
  }

  return {
    ...(requestId !== undefined ? { request_id: requestId } : {}),
    ...(traceId !== undefined ? { trace_id: traceId } : {}),
  };
};

const createTruncatedValueStub = (value: SmartCell["value"]): TruncatedValueStub => ({
  __truncated: true,
  size: getTruncatedValueSize(value),
  type: getTruncatedValueType(value),
  ...getTruncatedValueReferences(value),
});

const shouldClearStaleErrorPayload = (
  status: CellStatus,
  displayValue: string | null | undefined,
  value: SmartCell["value"] | undefined,
  existingValue: SmartCell["value"],
): boolean =>
  status === CellStatus.COMPLETED &&
  displayValue !== undefined &&
  value === undefined &&
  isObjectValue(existingValue) &&
  Object.prototype.hasOwnProperty.call(existingValue, "error");

const shouldPreserveExistingContentForPendingStatus = (
  status: CellStatus,
  column: SmartColumn | undefined,
): boolean => column?.type === "COMPOSITION" && PENDING_CELL_STATUSES.has(status);

const shouldPreserveExistingContentForStaleStatus = ({
  status,
  column,
  displayValue,
  value,
}: {
  status: CellStatus;
  column: SmartColumn | undefined;
  displayValue: string | null | undefined;
  value: SmartCell["value"] | undefined;
}): boolean =>
  status === CellStatus.STALE &&
  !isStableDisplayColumnType(column?.type) &&
  value === undefined &&
  (displayValue === undefined || displayValue === null);

interface CompositionConfigShape {
  sources?: unknown;
  source_titles?: unknown;
  source_types?: unknown;
  pending_removed_sources?: unknown;
}

const getCompositionSources = (config: CompositionConfigShape | undefined): string[] => {
  const sources = config?.sources;
  return Array.isArray(sources)
    ? sources.filter((source): source is string => typeof source === "string")
    : [];
};

const getPendingRemovedCompositionSources = (
  config: CompositionConfigShape | undefined,
): Set<string> => {
  const pendingRemovedSources = config?.pending_removed_sources;
  return new Set(
    Array.isArray(pendingRemovedSources)
      ? pendingRemovedSources.filter((source): source is string => typeof source === "string")
      : [],
  );
};

const getVisibleCompositionSources = (column: SmartColumn | undefined): string[] => {
  if (column?.type !== "COMPOSITION") {
    return [];
  }

  const config = column.config as CompositionConfigShape | undefined;
  return getCompositionSources(config);
};

const isStalenessExemptColumn = (
  column: SmartColumn | undefined,
  columns: ReadonlyMap<string, SmartColumn>,
): boolean => {
  if (!column || isStableDisplayColumnType(column.type)) {
    return true;
  }

  if (
    !isExecutionMetadataChildColumn(column) ||
    typeof column.group_parent_column_id !== "string"
  ) {
    return false;
  }

  return isReadonlyAnchorColumnType(columns.get(column.group_parent_column_id)?.type);
};

const getExecutionMetadataGroupColumnIds = (
  columnId: string,
  columns: ReadonlyMap<string, SmartColumn>,
): string[] => {
  const inputColumn = columns.get(columnId);

  const parentColumnId =
    inputColumn &&
    isTopLevelGroupedChildColumn(inputColumn) &&
    typeof inputColumn.group_parent_column_id === "string" &&
    inputColumn.group_parent_column_id.length > 0
      ? inputColumn.group_parent_column_id
      : columnId;

  const parentColumn = columns.get(parentColumnId);
  if (!parentColumn || !supportsExecutionMetadataColumns(parentColumn)) {
    return [parentColumnId];
  }

  return Array.from(
    new Set([
      parentColumnId,
      ...resolveGroupedChildIdsForParent(parentColumn, [...columns.values()]),
    ]),
  );
};

const getExpandedColumnIdsForRefresh = (
  columnId: string,
  columns: ReadonlyMap<string, SmartColumn>,
): string[] => {
  const column = columns.get(columnId);

  const compositionSources = getVisibleCompositionSources(column);
  if (compositionSources.length > 1) {
    return compositionSources.map(source => `${columnId}_${source}`);
  }

  const executionMetadataGroupColumnIds = getExecutionMetadataGroupColumnIds(columnId, columns);
  if (executionMetadataGroupColumnIds.length > 1) {
    return executionMetadataGroupColumnIds;
  }

  return [columnId];
};

const getDirectDependentColumnIds = (
  sourceColumnId: string,
  columns: ReadonlyMap<string, SmartColumn>,
): string[] => {
  const dependentColumnIds: string[] = [];

  for (const candidate of columns.values()) {
    if (!Array.isArray(candidate.dependencies) || candidate.dependencies.length === 0) {
      continue;
    }

    const dependsOnSource = candidate.dependencies.some(
      dependency => dependency.column_id === sourceColumnId,
    );

    if (dependsOnSource) {
      dependentColumnIds.push(candidate.id);
    }
  }

  return dependentColumnIds;
};

const getColumnsAffectedByStaleness = (
  columnIds: string[],
  columns: ReadonlyMap<string, SmartColumn>,
): string[] => {
  const pending = [...columnIds];
  const visited = new Set<string>();

  while (pending.length > 0) {
    const currentColumnId = pending.pop();
    if (!currentColumnId || visited.has(currentColumnId)) {
      continue;
    }

    visited.add(currentColumnId);

    const groupColumnIds = getExecutionMetadataGroupColumnIds(currentColumnId, columns);
    for (const groupColumnId of groupColumnIds) {
      if (!visited.has(groupColumnId)) {
        pending.push(groupColumnId);
      }
    }

    const dependentColumnIds = getDirectDependentColumnIds(currentColumnId, columns);
    for (const dependentColumnId of dependentColumnIds) {
      if (!visited.has(dependentColumnId)) {
        pending.push(dependentColumnId);
      }
    }
  }

  return [...visited];
};

const getExecutionMetadataValueFormatter = (
  metricKey: string | null | undefined,
): AgGridValueFormatter | undefined => {
  if (metricKey === "price") {
    return ({ value }) => formatExecutionMetadataPrice(value);
  }

  if (metricKey === "latency_ms") {
    return ({ value }) => formatExecutionMetadataLatency(value);
  }

  if (typeof metricKey === "string" && metricKey.length > 0) {
    return ({ value }) => formatExecutionMetadataStructuredValue(value);
  }

  return undefined;
};

interface CellExecutionOrder {
  executionId: string | null;
  status: CellStatus;
  updatedAt: string;
}

interface UpdateCellStatusOptions {
  displayValue?: string | null;
  error?: string | null;
  executionId?: string | null;
  updatedAt?: string;
  authoritative?: boolean;
}

const ORDERED_TERMINAL_CELL_STATUSES = new Set<CellStatus>([
  CellStatus.COMPLETED,
  CellStatus.FAILED,
  CellStatus.STALE,
]);

const CELL_STATUS_ORDER: Record<CellStatus, number> = {
  [CellStatus.QUEUED]: 0,
  [CellStatus.DISPATCHED]: 1,
  [CellStatus.RUNNING]: 2,
  [CellStatus.COMPLETED]: 3,
  [CellStatus.FAILED]: 3,
  [CellStatus.STALE]: 3,
};

const toTimestamp = (value: string | null | undefined): number => {
  if (!value) {
    return 0;
  }

  const timestamp = Date.parse(value);
  return Number.isFinite(timestamp) ? timestamp : 0;
};

const shouldAcceptOrderedCell = (
  current: CellExecutionOrder,
  incoming: CellExecutionOrder,
): boolean => {
  const currentIsPending = PENDING_CELL_STATUSES.has(current.status);
  const incomingIsTerminal = ORDERED_TERMINAL_CELL_STATUSES.has(incoming.status);
  const sameExecution =
    current.executionId !== null && incoming.executionId === current.executionId;
  const currentTimestamp = toTimestamp(current.updatedAt);
  const incomingTimestamp = toTimestamp(incoming.updatedAt);

  if (currentIsPending && incomingIsTerminal) {
    return (
      sameExecution ||
      (current.executionId === null && incoming.executionId === null) ||
      incomingTimestamp > currentTimestamp
    );
  }

  if (sameExecution) {
    if (CELL_STATUS_ORDER[incoming.status] < CELL_STATUS_ORDER[current.status]) {
      return false;
    }

    return (
      incomingTimestamp === 0 || currentTimestamp === 0 || incomingTimestamp >= currentTimestamp
    );
  }

  if (current.executionId === null && incoming.executionId === null) {
    return (
      incomingTimestamp === 0 || currentTimestamp === 0 || incomingTimestamp >= currentTimestamp
    );
  }

  return incomingTimestamp > currentTimestamp;
};

/**
 * How long a locally applied cell write (optimistic queue, WebSocket status or
 * content event, rollback) owns the cell outright against grid snapshots.
 *
 * Grid pages are served by a read replica that can lag the WebSocket stream,
 * and sheet version numbers only advance on run boundaries — a page with a
 * "newer" version can still predate the most recent WebSocket events (e.g. a
 * completion page racing the optimistic queue of the next run). Snapshots are
 * therefore never allowed to overwrite a cell written locally within this
 * window; reconciliation sweeps pick up anything genuinely missed once the
 * window passes.
 */
export const LOCAL_CELL_STATUS_AUTHORITY_MS = 15_000;

/**
 * Decide whether the locally cached cell should win over an incoming
 * snapshot cell.
 *
 * WebSocket events are the ordered source of truth while work is in flight:
 * a cell written locally within `LOCAL_CELL_STATUS_AUTHORITY_MS` is fully
 * owned by the WS/optimistic stream and snapshots may not modify it —
 * regardless of the snapshot's version, because version numbers cannot order
 * a page against individual WS events. Untouched cells reconcile freely so
 * filtered hydration, scrolling, and post-run sweeps keep working.
 */
const shouldPreserveLocalCell = (
  localCell: SmartCell | undefined,
  incomingCell: SmartCell,
  opts: { hasRecentLocalWriteAuthority: boolean },
): localCell is SmartCell => {
  if (localCell === undefined) {
    return false;
  }

  // A pending cell never regresses to STALE from a snapshot regardless of
  // recency: STALE is the state that queued the run in the first place.
  if (PENDING_CELL_STATUSES.has(localCell.status) && incomingCell.status === CellStatus.STALE) {
    return true;
  }

  if (
    opts.hasRecentLocalWriteAuthority &&
    PENDING_CELL_STATUSES.has(localCell.status) &&
    ORDERED_TERMINAL_CELL_STATUSES.has(incomingCell.status)
  ) {
    // A terminal snapshot for the execution currently shown locally is
    // authoritative even during the optimistic-write protection window.
    if (
      localCell.execution_id &&
      incomingCell.execution_id &&
      localCell.execution_id === incomingCell.execution_id
    ) {
      return false;
    }

    // A newer terminal result from a superseding execution also wins.
    if (
      localCell.execution_id &&
      incomingCell.execution_id &&
      toTimestamp(incomingCell.updated_at) > toTimestamp(localCell.updated_at)
    ) {
      return false;
    }
  }

  return opts.hasRecentLocalWriteAuthority;
};

const createCellFromStatusUpdate = ({
  rowIndex,
  columnId,
  status,
  displayValue,
  displayValueTruncated,
  value,
  serverId,
  executionId,
  updatedAt,
}: {
  rowIndex: number;
  columnId: string;
  status: CellStatus;
  displayValue?: string | null;
  displayValueTruncated?: boolean;
  value?: SmartCell["value"];
  serverId?: string;
  executionId?: string | null;
  updatedAt?: string;
}): SmartCell => ({
  id: serverId ?? `${rowIndex}-${columnId}`,
  row_index: rowIndex,
  smart_column_id: columnId,
  execution_id: executionId ?? null,
  status,
  display_value: displayValue ?? null,
  display_value_truncated: displayValueTruncated,
  value: value ?? null,
  input_hash: null,
  last_computed_version: null,
  updated_at: updatedAt ?? new Date().toISOString(),
});

const getTextCellValueToDisplay = (cell: SmartCell): unknown => {
  const { value } = cell;

  if (
    isOutputMediaPayload(value) ||
    isOutputMediaPayloadArray(value) ||
    isMultiContentValue(value)
  ) {
    return value;
  }

  if (cell.display_value !== null && cell.display_value !== undefined) {
    return cell.display_value;
  }

  if (value === null || value === undefined) {
    return "";
  }

  if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }

  if (isObjectValue(value) && "value" in value) {
    return value.value;
  }

  return value;
};

const filterAlignedCompositionMetadata = <T>(
  value: unknown,
  sources: string[],
  pendingRemovedSources: Set<string>,
): T[] | undefined => {
  if (!Array.isArray(value)) {
    return undefined;
  }

  const nextValue = value.filter((_, index) => !pendingRemovedSources.has(sources[index])) as T[];
  return nextValue.length > 0 ? nextValue : undefined;
};

const stableSerialize = (value: unknown): string => {
  if (value === undefined) {
    return "undefined";
  }

  if (value === null || typeof value !== "object") {
    return JSON.stringify(value) ?? "undefined";
  }

  if (Array.isArray(value)) {
    return `[${value.map(stableSerialize).join(",")}]`;
  }

  const entries = Object.entries(value as Record<string, unknown>).sort(([left], [right]) =>
    left.localeCompare(right),
  );

  return `{${entries
    .map(([key, nestedValue]) => `${JSON.stringify(key)}:${stableSerialize(nestedValue)}`)
    .join(",")}}`;
};

const areSmartColumnsEquivalent = (left: SmartColumn, right: SmartColumn): boolean =>
  left === right ||
  (left.id === right.id &&
    left.title === right.title &&
    left.position_rank === right.position_rank &&
    left.type === right.type &&
    left.config_hash === right.config_hash &&
    left.is_output_column === right.is_output_column &&
    left.is_virtual === right.is_virtual &&
    left.is_system_managed === right.is_system_managed &&
    left.system_kind === right.system_kind &&
    left.group_parent_column_id === right.group_parent_column_id &&
    left.metric_key === right.metric_key &&
    left.metric_label === right.metric_label &&
    stableSerialize(left.config) === stableSerialize(right.config) &&
    stableSerialize(left.dependencies) === stableSerialize(right.dependencies) &&
    stableSerialize(left.group_child_column_ids) ===
      stableSerialize(right.group_child_column_ids) &&
    stableSerialize(left.available_sources) === stableSerialize(right.available_sources) &&
    stableSerialize(left.sources) === stableSerialize(right.sources));

const finalizePendingRemovedCompositionSources = (column: SmartColumn): SmartColumn | null => {
  if (column.type !== "COMPOSITION") {
    return null;
  }

  const config = column.config as CompositionConfigShape | undefined;
  const sources = getCompositionSources(config);
  const pendingRemovedSources = getPendingRemovedCompositionSources(config);

  if (sources.length === 0 || pendingRemovedSources.size === 0) {
    return null;
  }

  const remainingSources = sources.filter(source => !pendingRemovedSources.has(source));
  const sourceTitles = filterAlignedCompositionMetadata<string>(
    config?.source_titles,
    sources,
    pendingRemovedSources,
  );
  const sourceTypes = filterAlignedCompositionMetadata<string>(
    config?.source_types,
    sources,
    pendingRemovedSources,
  );

  const { pending_removed_sources: _pendingRemovedSources, ...restConfig } = config ?? {};

  return {
    ...column,
    config: {
      ...restConfig,
      source: undefined,
      sources: remainingSources.length > 0 ? remainingSources : undefined,
      source_titles: sourceTitles,
      source_types: sourceTypes,
    },
  };
};

export interface ColumnUpdateSnapshot {
  columnId: string;
  column: SmartColumn | null;
}

export interface RowSnapshot {
  rowIndex: number;
  cells: Record<string, SmartCell>;
}

// ────────────────────────────────────────────
// AG Grid compatible types
// ────────────────────────────────────────────

export interface AgGridRowData {
  id: string;
  _rowIndex: number;
  [columnId: string]: unknown;
}

type DirtyRefreshReason = "value" | "status";

interface CellStatusUpdate {
  rowIndex: number;
  columnId: string;
  status: CellStatus;
  displayValue?: string | null;
  displayValueTruncated?: boolean;
  metadata?: SmartCell["metadata"];
  value?: SmartCell["value"];
  error?: string | null;
  serverId?: string;
  executionId?: string | null;
  updatedAt?: string;
}

const TERMINAL_CELL_STATUSES = new Set<CellStatus>([CellStatus.COMPLETED, CellStatus.FAILED]);

/**
 * Progression rank of a status within a single run. WebSocket status events
 * are published per-transition and can arrive out of order (e.g. DISPATCHED
 * landing after RUNNING or even after FAILED). A pending status may never
 * move a recently-touched cell *backwards* in this ordering — QUEUED is the
 * only pending status allowed to restart the cycle after a terminal status,
 * because it marks the start of a new run.
 */
const CELL_STATUS_RUN_RANK: Partial<Record<CellStatus, number>> = {
  [CellStatus.QUEUED]: 0,
  [CellStatus.DISPATCHED]: 1,
  [CellStatus.RUNNING]: 2,
  [CellStatus.COMPLETED]: 3,
  [CellStatus.FAILED]: 3,
};

interface StreamingDeltaBuffer {
  rowIndex: number;
  columnId: string;
  preview: string;
  totalSize: number;
  isTruncated: boolean;
  timerId: ReturnType<typeof setTimeout> | null;
}

const STREAMING_CELL_FLUSH_MS = 100;
const STREAMING_CELL_PREVIEW_CHAR_LIMIT = 2048;

// ────────────────────────────────────────────
// SheetDataStore
// ────────────────────────────────────────────

/**
 * Owns the in-memory representation of columns, rows, and cells for a
 * single mounted sheet. One instance per mounted sheet.
 *
 * The Server-Side Row Model datasource owns block loading and scroll-driven
 * fetches. This store is the local cache the datasource reads from, and is
 * also updated by optimistic mutations and WebSocket events.
 *
 * AG Grid is updated imperatively only where needed for structural or
 * targeted visual updates (e.g. column changes, row transactions, refreshes).
 */
export class SheetDataStore {
  columns: ObservableMap<string, SmartColumn> = observable.map();
  rows: ObservableMap<number, ObservableMap<string, SmartCell>> = observable.map();
  version = 0;
  reloadGeneration = 0;
  totalRows = 0;
  pendingDeletedColumnIds: Set<string> = observable.set<string>();
  pendingCreatedColumns: ObservableMap<string, PendingCreatedColumn> = observable.map();
  pendingCreatedRowIndices: Set<number> = observable.set<number>();
  pendingHydrationRowIndices: Set<number> = observable.set<number>();
  pendingHydrationColumnIds: Set<string> = observable.set<string>();

  private _gridApi: GridApi<AgGridRowData> | null = null;
  private _cellExecutionOrder = new Map<string, CellExecutionOrder>();
  private _pendingCellsAcrossReload = new Map<string, SmartCell>();

  /**
   * Unified RAF-based dirty-row buffer.
   *
   * Every store method that mutates cell data adds its affected row index
   * (and optionally column id) to these sets instead of calling
   * `applyServerSideTransaction` directly.  A single `requestAnimationFrame`
   * callback flushes ALL pending changes in one batched AG Grid transaction,
   * collapsing what used to be hundreds of per-cell transactions into ≤1 per
   * frame (~60 fps).
   */
  private _dirtyRows = new Set<number>();
  private _dirtyColumnsByRow = new Map<number, Set<string>>();
  private _dirtyRefreshReasonsByRow = new Map<number, Map<string, DirtyRefreshReason>>();
  private _rowsNeedingRedraw = new Set<number>();
  private _flushRafId: number | null = null;
  private _streamingDeltaBuffers = new Map<string, StreamingDeltaBuffer>();
  private _activeStatusFilterStatuses = new Set<CellStatus>();
  /**
   * Cell keys (`row:column`) whose status was applied locally (optimistic
   * queue, WebSocket status event, rollback), with the timestamp of the last
   * such write. Non-newer grid snapshots may not flip the status class of
   * these cells while the entry is fresh — see `LOCAL_CELL_STATUS_AUTHORITY_MS`.
   */
  private _localStatusTouchesByCellKey = new Map<string, number>();
  private _rowPayloadBlockAccessedAt = new Map<number, number>();
  private _evictedRowPayloadIndices = new Set<number>();
  private _rowPayloadRetentionBlockSize = DEFAULT_ROW_PAYLOAD_RETENTION_BLOCK_SIZE;
  private _rowPayloadAccessClock = 0;
  private _isGridRefreshSuspended = false;
  needsRefreshOnActivate = false;

  constructor() {
    makeAutoObservable<
      SheetDataStore,
      | "_dirtyRows"
      | "_dirtyColumnsByRow"
      | "_dirtyRefreshReasonsByRow"
      | "_rowsNeedingRedraw"
      | "_flushRafId"
      | "_cellExecutionOrder"
      | "_pendingCellsAcrossReload"
      | "_streamingDeltaBuffers"
      | "_activeStatusFilterStatuses"
      | "_localStatusTouchesByCellKey"
      | "_rowPayloadBlockAccessedAt"
      | "_evictedRowPayloadIndices"
      | "_rowPayloadRetentionBlockSize"
      | "_rowPayloadAccessClock"
      | "_isGridRefreshSuspended"
      | "_flushDirtyRows"
      | "_flushStreamingCell"
      | "_recordDirtyColumn"
      | "_scheduleDirtyFlush"
    >(this, {
      columns: observable,
      rows: observable,
      pendingDeletedColumnIds: observable,
      pendingCreatedColumns: observable,
      pendingCreatedRowIndices: observable,
      pendingHydrationRowIndices: observable,
      pendingHydrationColumnIds: observable,
      needsRefreshOnActivate: observable,
      sortedColumns: computed.struct,
      agGridColumnDefs: computed.struct,
      agGridRowData: computed,
      hasComputedColumns: computed,
      totalComputedRowCount: computed,
      allRowIndices: computed,
      pendingRowIndices: computed,
      failedCount: computed,
      failedRowIndices: computed,
      gridApi: false,
      _dirtyRows: false,
      _dirtyColumnsByRow: false,
      _dirtyRefreshReasonsByRow: false,
      _rowsNeedingRedraw: false,
      _flushRafId: false,
      _cellExecutionOrder: false,
      _pendingCellsAcrossReload: false,
      _streamingDeltaBuffers: false,
      _activeStatusFilterStatuses: false,
      _localStatusTouchesByCellKey: false,
      _rowPayloadBlockAccessedAt: false,
      _evictedRowPayloadIndices: false,
      _rowPayloadRetentionBlockSize: false,
      _rowPayloadAccessClock: false,
      _isGridRefreshSuspended: false,
      _flushDirtyRows: false,
      _flushStreamingCell: false,
      _recordDirtyColumn: false,
      _scheduleDirtyFlush: false,
    });
  }

  // ── Status-filtered SSRM transactions ─────

  setActiveStatusFilterStatuses = (statuses: CellStatus[]): void => {
    this._activeStatusFilterStatuses = new Set(statuses);
    traceSmartTable({
      category: "filter",
      op: "set-active-status-filter",
      source: "sheet-data-store:setActiveStatusFilterStatuses",
      detail: { statuses },
    });
  };

  /**
   * Row-level status filter membership. Mirrors the backend's `status_filter`
   * semantics exactly: a row matches when any cell in a computed (non-TEXT,
   * non-COMPOSITION) column has one of the selected statuses.
   */
  private _rowMatchesActiveStatusFilter = (rowIndex: number): boolean => {
    if (this._activeStatusFilterStatuses.size === 0) {
      return true;
    }

    const row = this.rows.get(rowIndex);
    if (!row) {
      return true;
    }

    for (const [columnId, cell] of row.entries()) {
      const column = this.columns.get(columnId);
      if (!column || column.type === ColumnType.TEXT || column.type === ColumnType.COMPOSITION) {
        continue;
      }

      if (this._activeStatusFilterStatuses.has(cell.status)) {
        return true;
      }
    }

    return false;
  };

  /**
   * Atomically remove served rows that do not match the active status filter.
   *
   * Called by the SSRM datasource right after a filtered block is ingested.
   * The backend owns filter membership, but its snapshot can lag the
   * WebSocket stream (read replica). Any such stale row is removed in the
   * same frame it is served, so a lagging block can never resurrect rows the
   * live stream has already moved past the filter.
   */
  removeServedRowsNotMatchingActiveStatusFilter = (rowIndices: number[]): void => {
    if (!this._gridApi || this._activeStatusFilterStatuses.size === 0) {
      return;
    }

    const rowsToRemove = rowIndices
      .filter(rowIndex => !this._rowMatchesActiveStatusFilter(rowIndex))
      .map(rowIndex => this._buildRowData(rowIndex))
      .filter(row => this._gridApi?.getRowNode(row.id));

    if (rowsToRemove.length === 0) {
      return;
    }

    this._gridApi.applyServerSideTransaction({ remove: rowsToRemove });
    this.setKnownTotalRows(Math.max(0, this.totalRows - rowsToRemove.length));
    traceSmartTableRows({
      category: "grid-op",
      op: "applyServerSideTransaction:remove",
      source: "sheet-data-store:removeServedRowsNotMatchingActiveStatusFilter",
      rowIndices: rowsToRemove.map(row => row._rowIndex),
      detail: { reason: "served-row-does-not-match-status-filter" },
    });
  };

  // ── Local status authority ────────────────

  private _localStatusTouchKey = (rowIndex: number, columnId: string): string =>
    `${rowIndex}\u0000${columnId}`;

  private _recordLocalStatusTouch = (rowIndex: number, columnId: string): void => {
    // Opportunistically drop expired entries so the map stays bounded on
    // long-lived sheets with many runs.
    if (this._localStatusTouchesByCellKey.size >= 10_000) {
      const cutoff = Date.now() - LOCAL_CELL_STATUS_AUTHORITY_MS;
      for (const [key, touchedAt] of this._localStatusTouchesByCellKey) {
        if (touchedAt < cutoff) {
          this._localStatusTouchesByCellKey.delete(key);
        }
      }
    }

    this._localStatusTouchesByCellKey.set(
      this._localStatusTouchKey(rowIndex, columnId),
      Date.now(),
    );
  };

  private _hasRecentLocalStatusTouch = (
    rowIndex: number,
    columnId: string,
    now: number,
  ): boolean => {
    const touchedAt = this._localStatusTouchesByCellKey.get(
      this._localStatusTouchKey(rowIndex, columnId),
    );
    return touchedAt !== undefined && now - touchedAt < LOCAL_CELL_STATUS_AUTHORITY_MS;
  };

  /**
   * True when an incoming pending status would move a recently-updated cell
   * backwards in the run progression (e.g. DISPATCHED arriving after RUNNING
   * or after a terminal status). Such events are stale, out-of-order
   * deliveries and must not repaint the cell. QUEUED is exempt — it starts a
   * new run. Once the local-authority window has passed, the guard stands
   * down so a dropped-QUEUED run can still surface its pending states.
   */
  private _isStaleOutOfOrderStatusTransition = (
    rowIndex: number,
    columnId: string,
    currentStatus: CellStatus,
    incomingStatus: CellStatus,
  ): boolean => {
    if (incomingStatus === CellStatus.QUEUED || !PENDING_CELL_STATUSES.has(incomingStatus)) {
      return false;
    }

    const currentRank = CELL_STATUS_RUN_RANK[currentStatus];
    const incomingRank = CELL_STATUS_RUN_RANK[incomingStatus];
    if (currentRank === undefined || incomingRank === undefined || incomingRank >= currentRank) {
      return false;
    }

    return this._hasRecentLocalStatusTouch(rowIndex, columnId, Date.now());
  };

  // ── Grid API wiring ───────────────────────

  setGridApi = (gridApi: GridApi<AgGridRowData> | null): void => {
    this._gridApi = gridApi;
  };

  get gridApi(): GridApi<AgGridRowData> | null {
    return this._gridApi;
  }

  setGridRefreshSuspended = (isSuspended: boolean): void => {
    this._isGridRefreshSuspended = isSuspended;
  };

  markNeedsRefreshOnActivate = (): void => {
    this.needsRefreshOnActivate = true;
  };

  consumeNeedsRefreshOnActivate = (): boolean => {
    const needsRefresh = this.needsRefreshOnActivate;
    this.needsRefreshOnActivate = false;
    return needsRefresh;
  };

  get isGridRefreshSuspended(): boolean {
    return this._isGridRefreshSuspended;
  }

  private _getPayloadBlockIndex = (rowIndex: number): number =>
    Math.floor(rowIndex / this._rowPayloadRetentionBlockSize);

  private _recordRowPayloadAccess = (startRow: number, endRow: number): void => {
    if (endRow <= startRow) {
      return;
    }

    const firstBlock = this._getPayloadBlockIndex(Math.max(0, startRow));
    const lastBlock = this._getPayloadBlockIndex(Math.max(0, endRow - 1));
    for (let blockIndex = firstBlock; blockIndex <= lastBlock; blockIndex += 1) {
      this._rowPayloadAccessClock += 1;
      this._rowPayloadBlockAccessedAt.set(blockIndex, this._rowPayloadAccessClock);
    }
  };

  private _truncateRowPayload = (rowIndex: number): void => {
    const cellMap = this.rows.get(rowIndex);
    if (!cellMap) {
      return;
    }

    for (const [columnId, cell] of cellMap.entries()) {
      cellMap.set(columnId, {
        ...cell,
        value: createTruncatedValueStub(cell.value),
      });
    }
    this._evictedRowPayloadIndices.add(rowIndex);
  };

  private _evictRowPayloadsOutsideRetentionWindow = (): void => {
    const maxBlocks = getSmartTableRowPayloadRetentionBlocks();
    if (this._rowPayloadBlockAccessedAt.size <= maxBlocks) {
      return;
    }

    const retainedBlocks = new Set(
      Array.from(this._rowPayloadBlockAccessedAt.entries())
        .sort(([, a], [, b]) => b - a)
        .slice(0, maxBlocks)
        .map(([blockIndex]) => blockIndex),
    );

    const truncatedRowIndices: number[] = [];
    for (const blockIndex of Array.from(this._rowPayloadBlockAccessedAt.keys())) {
      if (retainedBlocks.has(blockIndex)) {
        continue;
      }

      this._rowPayloadBlockAccessedAt.delete(blockIndex);
      const startRow = blockIndex * this._rowPayloadRetentionBlockSize;
      const endRow = startRow + this._rowPayloadRetentionBlockSize;
      for (let rowIndex = startRow; rowIndex < endRow; rowIndex += 1) {
        if (
          this.pendingCreatedRowIndices.has(rowIndex) ||
          this.pendingHydrationRowIndices.has(rowIndex)
        ) {
          continue;
        }
        if (this.rows.has(rowIndex) && !this._evictedRowPayloadIndices.has(rowIndex)) {
          truncatedRowIndices.push(rowIndex);
        }
        this._truncateRowPayload(rowIndex);
      }
    }

    this._syncTruncatedRowsIntoGrid(truncatedRowIndices);
  };

  /**
   * Push freshly stubbed rows into AG Grid's block cache so its cached rows
   * stop pinning full payloads. This keeps memory bounded by the store's
   * retention window while letting AG Grid retain every visited block — which
   * is what prevents purge-driven loading shimmer when scrolling back.
   */
  private _syncTruncatedRowsIntoGrid = (rowIndices: number[]): void => {
    if (rowIndices.length === 0 || !this._gridApi || this._isGridRefreshSuspended) {
      return;
    }

    const rowsToUpdate: AgGridRowData[] = [];
    for (const rowIndex of rowIndices) {
      if (this._gridApi.getRowNode(String(rowIndex))) {
        rowsToUpdate.push(this._buildRowData(rowIndex));
      }
    }

    if (rowsToUpdate.length === 0) {
      return;
    }

    this._gridApi.applyServerSideTransaction({ update: rowsToUpdate });
  };

  /**
   * Push the store's current state for the given rows into AG Grid in place:
   * a row-data transaction plus a forced, flash-suppressed cell refresh.
   *
   * This is the reconciliation primitive — unlike `refreshServerSide`, it
   * never reloads blocks, so row nodes and React cell renderers are reused
   * and pending-cell pulse animations do not restart.
   */
  applyLoadedRowsToGrid = (
    rowIndices: number[],
    source = "sheet-data-store:applyLoadedRowsToGrid",
  ): void => {
    if (rowIndices.length === 0 || !this._gridApi) {
      return;
    }

    if (this._isGridRefreshSuspended) {
      this.markNeedsRefreshOnActivate();
      return;
    }

    const rowsToUpdate: AgGridRowData[] = [];
    const rowNodes: IRowNode[] = [];
    for (const rowIndex of rowIndices) {
      if (!this.rows.has(rowIndex)) {
        continue;
      }

      const rowNode = this._gridApi.getRowNode(String(rowIndex));
      if (!rowNode) {
        continue;
      }

      rowsToUpdate.push(this._buildRowData(rowIndex));
      rowNodes.push(rowNode);
    }

    if (rowsToUpdate.length === 0) {
      return;
    }

    this._gridApi.applyServerSideTransaction({ update: rowsToUpdate });
    this._gridApi.refreshCells({
      rowNodes,
      force: true,
      suppressFlash: true,
    });
    traceSmartTableRows({
      category: "grid-op",
      op: "applyServerSideTransaction:update+refreshCells",
      source,
      rowIndices: rowsToUpdate.map(row => row._rowIndex),
    });
  };

  /**
   * Clear local SSRM-backed cache in the store and force AG Grid to reload
   * all server-side blocks from scratch.
   */
  clearLocalCacheAndReloadFromServer = (
    knownRowCount?: number | null,
    options?: { clearColumns?: boolean; refreshGrid?: boolean },
  ): void => {
    traceSmartTable({
      category: "grid-op",
      op: "clearLocalCacheAndReloadFromServer",
      source: "sheet-data-store:clearLocalCacheAndReloadFromServer",
      detail: {
        knownRowCount,
        clearColumns: options?.clearColumns,
        refreshGrid: options?.refreshGrid,
      },
    });

    const preservedRowCount =
      typeof knownRowCount === "number" && Number.isFinite(knownRowCount)
        ? Math.max(0, Math.trunc(knownRowCount))
        : this.totalRows;

    this.reloadGeneration += 1;
    this.rows.clear();
    this._rowPayloadBlockAccessedAt.clear();
    this._evictedRowPayloadIndices.clear();
    this._localStatusTouchesByCellKey.clear();
    this.totalRows = preservedRowCount;
    this.version = 0;
    this.pendingCreatedRowIndices.clear();
    this.pendingHydrationRowIndices.clear();
    this.pendingHydrationColumnIds.clear();

    if (options?.clearColumns) {
      this.columns.clear();
      this.pendingDeletedColumnIds.clear();
      this.pendingCreatedColumns.clear();
    }

    if (options?.refreshGrid !== false && this._gridApi) {
      if (this._isGridRefreshSuspended) {
        this.markNeedsRefreshOnActivate();
        return;
      }

      requestAnimationFrame(() => {
        traceSmartTable({
          category: "grid-op",
          op: "refreshServerSide:purge",
          source: "sheet-data-store:clearLocalCacheAndReloadFromServer",
        });
        this._gridApi?.refreshServerSide({ route: [], purge: true });
      });
    }
  };

  setKnownTotalRows = (rowCount: number): boolean => {
    if (!Number.isFinite(rowCount)) {
      return false;
    }

    const normalizedCount = Math.max(0, Math.trunc(rowCount));
    if (
      normalizedCount < this.totalRows &&
      (this.pendingHydrationRowIndices.size > 0 || this.pendingCreatedRowIndices.size > 0)
    ) {
      return false;
    }

    if (normalizedCount !== this.totalRows) {
      this.totalRows = normalizedCount;
      return true;
    }

    return false;
  };

  private _cellOrderKey = (rowIndex: number, columnId: string): string => `${rowIndex}:${columnId}`;

  private _getCellExecutionOrder = (
    rowIndex: number,
    columnId: string,
    cell?: SmartCell,
  ): CellExecutionOrder | null => {
    const recorded = this._cellExecutionOrder.get(this._cellOrderKey(rowIndex, columnId));
    if (recorded) {
      return recorded;
    }

    if (!cell) {
      return null;
    }

    return {
      executionId: cell.execution_id ?? null,
      status: cell.status,
      updatedAt: cell.updated_at,
    };
  };

  private _recordCellExecutionOrder = (cell: SmartCell): void => {
    const orderKey = this._cellOrderKey(cell.row_index, cell.smart_column_id);
    this._cellExecutionOrder.set(orderKey, {
      executionId: cell.execution_id ?? null,
      status: cell.status,
      updatedAt: cell.updated_at,
    });

    if (PENDING_CELL_STATUSES.has(cell.status)) {
      this._pendingCellsAcrossReload.set(orderKey, { ...cell });
    } else {
      this._pendingCellsAcrossReload.delete(orderKey);
    }
  };

  private _shouldAcceptAuthoritativeCell = (
    rowIndex: number,
    columnId: string,
    incomingCell: SmartCell,
    currentCell?: SmartCell,
  ): boolean => {
    const currentOrder = this._getCellExecutionOrder(rowIndex, columnId, currentCell);
    if (!currentOrder) {
      return true;
    }

    return shouldAcceptOrderedCell(currentOrder, {
      executionId: incomingCell.execution_id ?? null,
      status: incomingCell.status,
      updatedAt: incomingCell.updated_at,
    });
  };

  private _mergeAuthoritativeCells = (
    target: ObservableMap<string, SmartCell>,
    source: Record<string, SmartCell>,
    visibleColumnIds: ReadonlySet<string>,
    existingCells?: ReadonlyMap<string, SmartCell>,
  ): void => {
    for (const [columnId, incomingCell] of Object.entries(source)) {
      if (!visibleColumnIds.has(columnId)) {
        continue;
      }

      const currentCell =
        target.get(columnId) ??
        existingCells?.get(columnId) ??
        this._pendingCellsAcrossReload.get(this._cellOrderKey(incomingCell.row_index, columnId));
      if (
        shouldPreserveLocalCell(currentCell, incomingCell, {
          hasRecentLocalWriteAuthority: this._hasRecentLocalStatusTouch(
            incomingCell.row_index,
            columnId,
            Date.now(),
          ),
        })
      ) {
        target.set(columnId, currentCell);
        continue;
      }
      target.set(columnId, incomingCell);
      this._recordCellExecutionOrder(incomingCell);
    }
  };

  // ── Loading ───────────────────────────────

  canEditCell = (rowIndex: number, columnId: string): boolean => {
    const cell = this.rows.get(rowIndex)?.get(columnId);
    if (!cell) {
      return false;
    }

    if (
      this.pendingHydrationRowIndices.has(rowIndex) ||
      this.pendingHydrationColumnIds.has(columnId)
    ) {
      return false;
    }

    return !cell.id.startsWith("temp-");
  };

  private _buildPlaceholderCell = (rowIndex: number, columnId: string): SmartCell => {
    const column = this.columns.get(columnId);
    const columnType = column?.type;
    const isStableDisplayColumn = isStableDisplayColumnType(columnType);
    const isStalenessExempt = isStalenessExemptColumn(column, this.columns);

    return {
      id: `temp-${rowIndex}-${columnId}`,
      row_index: rowIndex,
      smart_column_id: columnId,
      status: isStalenessExempt ? CellStatus.COMPLETED : CellStatus.STALE,
      display_value: isStableDisplayColumn ? "" : null,
      value: isStableDisplayColumn ? { value: "" } : null,
      input_hash: null,
      last_computed_version: null,
      updated_at: new Date().toISOString(),
    };
  };

  private _cloneCellForColumn = (cell: SmartCell, columnId: string): SmartCell => ({
    ...cell,
    id: `temp-${cell.row_index}-${columnId}`,
    smart_column_id: columnId,
    value: copyCellValueForColumnDuplicate(cell.value),
    updated_at: new Date().toISOString(),
  });

  // ── Internal AG Grid helpers ─────────────

  private _buildRowData = (rowIndex: number): AgGridRowData => {
    const cellMap = this.rows.get(rowIndex);
    const isPendingHydration = this.pendingHydrationRowIndices.has(rowIndex);
    const row: AgGridRowData = {
      id: String(rowIndex),
      _rowIndex: rowIndex,
      _pendingHydration: isPendingHydration,
    };

    if (!cellMap) {
      return row;
    }

    for (const [colId, cell] of (cellMap as ObservableMap<string, SmartCell>).entries()) {
      const column = this.columns.get(colId);
      const isStableDisplayColumn = isStableDisplayColumnType(column?.type);
      const displayState = isStableDisplayColumn ? null : resolveCellDisplayState(cell);
      let valueToDisplay = isStableDisplayColumn
        ? getTextCellValueToDisplay(cell)
        : displayState?.valueToDisplay;
      const errorMessage = isStableDisplayColumn ? undefined : displayState?.errorMessage;
      const visibleCompositionSources =
        column?.type === "COMPOSITION" ? getVisibleCompositionSources(column) : [];

      let compositionParentValue = valueToDisplay;

      if (column?.type === "COMPOSITION" && !isExecutionMetadataChildColumn(column)) {
        const rawValue = cell.value;
        if (isObjectValue(rawValue) && "value" in rawValue) {
          compositionParentValue = rawValue.value;
        } else if (rawValue !== null && rawValue !== undefined) {
          compositionParentValue = rawValue;
        }

        compositionParentValue = normalizeCompositionDisplayValue(compositionParentValue);
        valueToDisplay = resolveCompositionDisplayValue(
          column,
          compositionParentValue,
          valueToDisplay,
          {
            errorMessage,
            failed: cell.status === CellStatus.FAILED,
          },
        );
      }

      row[colId] = valueToDisplay;
      row[`_cellId_${colId}`] = cell.id;
      row[`_pendingHydration_${colId}`] =
        isPendingHydration || this.pendingHydrationColumnIds.has(colId);

      if (visibleCompositionSources.length > 1) {
        assignCompositionSourceFields(
          row as Record<string, unknown>,
          column ?? null,
          compositionParentValue,
        );
      }
      row[`_status_${colId}`] = isStalenessExemptColumn(column, this.columns)
        ? CellStatus.COMPLETED
        : cell.status;
      row[`_error_${colId}`] =
        column?.type === "COMPOSITION" &&
        visibleCompositionSources.length > 1 &&
        cell.status === CellStatus.STALE
          ? undefined
          : errorMessage;
      row[`_value_${colId}`] = cell.value;
      row[`_displayTruncated_${colId}`] = cell.display_value_truncated === true;
      row[`_metadata_${colId}`] = cell.metadata;
    }

    return row;
  };

  private _buildRowDataFromCells = (
    rowIndex: number,
    cells: Record<string, SmartCell> | ObservableMap<string, SmartCell>,
  ): AgGridRowData => {
    const isPendingHydration = this.pendingHydrationRowIndices.has(rowIndex);
    const row: AgGridRowData = {
      id: String(rowIndex),
      _rowIndex: rowIndex,
      _pendingHydration: isPendingHydration,
    };

    const assignCellData = (colId: string, cell: SmartCell) => {
      const column = this.columns.get(colId);
      const isStableDisplayColumn = isStableDisplayColumnType(column?.type);
      const displayState = isStableDisplayColumn ? null : resolveCellDisplayState(cell);
      const valueToDisplay = isStableDisplayColumn
        ? getTextCellValueToDisplay(cell)
        : displayState?.valueToDisplay;
      const errorMessage = isStableDisplayColumn ? undefined : displayState?.errorMessage;

      row[colId] = valueToDisplay;
      row[`_cellId_${colId}`] = cell.id;
      row[`_pendingHydration_${colId}`] =
        isPendingHydration || this.pendingHydrationColumnIds.has(colId);
      row[`_status_${colId}`] = isStalenessExemptColumn(column, this.columns)
        ? CellStatus.COMPLETED
        : cell.status;
      row[`_error_${colId}`] = errorMessage;
      row[`_value_${colId}`] = cell.value;
      row[`_displayTruncated_${colId}`] = cell.display_value_truncated === true;
      row[`_metadata_${colId}`] = cell.metadata;
    };

    if (cells instanceof ObservableMap) {
      for (const [colId, cell] of cells.entries()) {
        assignCellData(colId, cell);
      }
      return row;
    }

    for (const [colId, cell] of Object.entries(cells)) {
      assignCellData(colId, cell);
    }

    return row;
  };

  toAgGridRowData = (rowIndex: number): AgGridRowData => this._buildRowData(rowIndex);

  getAgGridRowDataRange = (startRow: number, endRow: number): AgGridRowData[] => {
    this._recordRowPayloadAccess(startRow, endRow);

    // Evicted rows still hold status + display previews + truncated value
    // stubs — the same shape as server-truncated cells: fully renderable, with
    // full values fetched on demand (dialog open, copy, fullscreen edit).
    // Serving them from cache avoids redundant refetches and loading shimmer
    // when scrolling back to previously visited blocks.
    const rows: AgGridRowData[] = [];
    for (let rowIndex = startRow; rowIndex < endRow; rowIndex += 1) {
      if (!this.rows.has(rowIndex)) {
        break;
      }
      rows.push(this._buildRowData(rowIndex));
    }
    return rows;
  };

  private _getRowIndicesForColumnRefresh = (
    columnIds: string[],
    dirtyRowIndices?: number[],
  ): number[] => {
    const hasAffectedCell = (rowIndex: number): boolean => {
      const row = this.rows.get(rowIndex);
      return Boolean(row && columnIds.some(columnId => row.has(columnId)));
    };

    if (dirtyRowIndices) {
      return dirtyRowIndices.filter(hasAffectedCell);
    }

    if (!this._gridApi) {
      return (Array.from(this.rows.keys()) as number[]).filter(hasAffectedCell);
    }

    const firstDisplayedRowIndex = this._gridApi.getFirstDisplayedRowIndex();
    const lastDisplayedRowIndex = this._gridApi.getLastDisplayedRowIndex();
    if (firstDisplayedRowIndex < 0 || lastDisplayedRowIndex < firstDisplayedRowIndex) {
      return [];
    }

    const rowIndices: number[] = [];
    for (
      let displayedRowIndex = firstDisplayedRowIndex;
      displayedRowIndex <= lastDisplayedRowIndex;
      displayedRowIndex += 1
    ) {
      const rowIndex = this._gridApi.getDisplayedRowAtIndex(displayedRowIndex)?.data?._rowIndex;
      if (typeof rowIndex === "number" && Number.isFinite(rowIndex) && hasAffectedCell(rowIndex)) {
        rowIndices.push(rowIndex);
      }
    }

    return rowIndices;
  };

  private _refreshCellsForColumn = (columnId: string, dirtyRowIndices?: number[]): void => {
    this._refreshCellsForColumns([columnId], dirtyRowIndices);
  };

  private _refreshCellsForColumns = (columnIds: string[], dirtyRowIndices?: number[]): void => {
    if (!this._gridApi || columnIds.length === 0) {
      return;
    }

    if (this._isGridRefreshSuspended) {
      this.markNeedsRefreshOnActivate();
      return;
    }

    const rowIndices = this._getRowIndicesForColumnRefresh(columnIds, dirtyRowIndices);
    if (rowIndices.length === 0) {
      return;
    }

    const rowsToUpdate = rowIndices.map(rowIndex => this._buildRowData(rowIndex));

    this._gridApi.applyServerSideTransaction({ update: rowsToUpdate });

    const rowNodes = rowIndices
      .map(idx => this._gridApi?.getRowNode(String(idx)))
      .filter((rowNode): rowNode is IRowNode => Boolean(rowNode));
    if (rowNodes.length > 0) {
      const expandedColumnIds = columnIds.flatMap(colId =>
        getExpandedColumnIdsForRefresh(colId, this.columns),
      );

      this._gridApi.refreshCells({
        rowNodes,
        columns: expandedColumnIds,
        // Status-only column stale/queue changes update sibling metadata fields read by renderers.
        force: true,
        suppressFlash: true,
      });
    }
  };

  // ── Hydration ────────────────────────────

  /**
   * Append a page of rows from the server response into local store state.
   * The SSRM datasource reads from this state; we intentionally do not push
   * client-side transactions into AG Grid here.
   *
   * Pages are always accepted — even when their version trails the version
   * the client learned over the WebSocket — so row hydration can never be
   * starved by a lagging read replica. Freshly written cells are protected
   * per-cell by the local write-authority window instead (see
   * `LOCAL_CELL_STATUS_AUTHORITY_MS`); rejecting whole pages here previously
   * left AG Grid blocks marked as loaded while their rows rendered as
   * permanently pending hydration placeholders.
   */
  appendPage = (gridResponse: GridResponse): void => {
    const visibleColumnIds = new Set<string>(
      (Array.from(this.columns.keys()) as string[]).filter(
        columnId => !this.pendingDeletedColumnIds.has(columnId),
      ),
    );
    const hydratedColumnIds = new Set<string>();
    const matchedPendingCreatedTempIds = new Set<string>();

    for (const col of gridResponse.columns ?? []) {
      if (this.pendingDeletedColumnIds.has(col.id)) {
        continue;
      }

      const pendingCreatedEntries = Array.from(this.pendingCreatedColumns.entries()) as [
        string,
        PendingCreatedColumn,
      ][];
      const matchingPendingCreatedEntry = pendingCreatedEntries.find(
        ([, pendingCreated]) =>
          pendingCreated.title === col.title && pendingCreated.positionRank === col.position_rank,
      );

      if (matchingPendingCreatedEntry) {
        const [tempId] = matchingPendingCreatedEntry;
        matchedPendingCreatedTempIds.add(tempId);
      }

      const existing = this.columns.get(col.id);
      if (!existing || !areSmartColumnsEquivalent(existing, col)) {
        this.columns.set(col.id, col);
      }
      visibleColumnIds.add(col.id);
      hydratedColumnIds.add(col.id);
    }

    for (const [tempId, pendingCreated] of this.pendingCreatedColumns.entries()) {
      if (matchedPendingCreatedTempIds.has(tempId)) {
        continue;
      }

      this.columns.set(tempId, pendingCreated.column);
      visibleColumnIds.add(tempId);
    }

    const requestedRowCount = Math.max(1, gridResponse.limit || gridResponse.rows.length || 1);
    this._rowPayloadRetentionBlockSize = requestedRowCount;
    if (gridResponse.rows.length > 0) {
      const rowIndices = gridResponse.rows.map(row => row.row_index);
      this._recordRowPayloadAccess(Math.min(...rowIndices), Math.max(...rowIndices) + 1);
    }

    for (const row of gridResponse.rows) {
      const shouldHydrateExistingRow =
        this.rows.has(row.row_index) &&
        (this.pendingHydrationRowIndices.has(row.row_index) ||
          this.pendingCreatedRowIndices.has(row.row_index));

      if (!this.rows.has(row.row_index) || shouldHydrateExistingRow) {
        const existingCellMap = this.rows.get(row.row_index) as
          | ObservableMap<string, SmartCell>
          | undefined;
        const cellMap: ObservableMap<string, SmartCell> = observable.map();
        this._mergeAuthoritativeCells(cellMap, row.cells, visibleColumnIds, existingCellMap);

        for (const [tempId, pendingCreated] of this.pendingCreatedColumns.entries()) {
          if (matchedPendingCreatedTempIds.has(tempId)) {
            continue;
          }

          if (!cellMap.has(tempId)) {
            const sourceCell = pendingCreated.sourceColumnId
              ? cellMap.get(pendingCreated.sourceColumnId)
              : undefined;
            cellMap.set(
              tempId,
              sourceCell
                ? this._cloneCellForColumn(sourceCell, tempId)
                : this._buildPlaceholderCell(row.row_index, pendingCreated.tempId),
            );
          }
        }

        this.rows.set(row.row_index, cellMap);
        this._evictedRowPayloadIndices.delete(row.row_index);
        this.pendingCreatedRowIndices.delete(row.row_index);
        this.pendingHydrationRowIndices.delete(row.row_index);
      } else {
        const existingCellMap = this.rows.get(row.row_index);
        if (existingCellMap) {
          this._mergeAuthoritativeCells(existingCellMap, row.cells, visibleColumnIds);
          this._evictedRowPayloadIndices.delete(row.row_index);
        }
      }
    }

    this._evictRowPayloadsOutsideRetentionWindow();

    for (const columnId of hydratedColumnIds) {
      this.pendingHydrationColumnIds.delete(columnId);
    }

    for (const tempId of matchedPendingCreatedTempIds) {
      this.pendingCreatedColumns.delete(tempId);
    }

    if (typeof gridResponse.row_count === "number") {
      this.setKnownTotalRows(gridResponse.row_count);
    }

    if (gridResponse.version > this.version) {
      this.version = gridResponse.version;
    }
  };

  // ── Cell helpers ─────────────────────────

  // O(rows × cols). Fine for current fallback paths; add a cellId → coords index if
  // websocket/reconcile profiling shows this on hot paths for larger sheets.
  private _findCellById(
    cellId: string,
  ): { rowIndex: number; columnId: string; cell: SmartCell } | null {
    for (const [rowIndex, cellMap] of this.rows.entries()) {
      for (const [columnId, cell] of cellMap.entries()) {
        if (cell.id === cellId) {
          return { rowIndex, columnId, cell };
        }
      }
    }
    return null;
  }

  /**
   * Look up a cell directly by its (rowIndex, columnId) coordinates.
   *
   * Use this instead of `_findCellById` when you already know the row and
   * column — it is O(1) vs O(n·m) and correctly handles placeholder cells
   * whose synthetic `id` fields do not match server-assigned UUIDs.
   */
  private _findCellByCoords(
    rowIndex: number,
    columnId: string,
  ): { rowIndex: number; columnId: string; cell: SmartCell } | null {
    const cellMap = this.rows.get(rowIndex) as ObservableMap<string, SmartCell> | undefined;
    if (!cellMap) {
      return null;
    }
    const cell = cellMap.get(columnId);
    if (!cell) {
      return null;
    }
    return { rowIndex, columnId, cell };
  }

  /**
   * Capture a snapshot of a cell before mutation (for rollback).
   */
  getCellSnapshot = (cellId: string): CellSnapshot => {
    const found = this._findCellById(cellId);
    if (!found) {
      return { cellId, cell: null, rowIndex: -1, columnId: "" };
    }
    return {
      cellId,
      cell: { ...found.cell },
      rowIndex: found.rowIndex,
      columnId: found.columnId,
    };
  };

  /**
   * Optimistic cell update — instant local apply before network call.
   */
  updateCell = (
    cellId: string,
    displayValue: string | null,
    value?: SmartCell["value"],
    previewOptions:
      | boolean
      | {
          displayValueTruncated?: boolean;
          metadata?: SmartCell["metadata"];
        } = false,
  ): void => {
    const found = this._findCellById(cellId);
    if (!found) {
      return;
    }

    const displayValueTruncated =
      typeof previewOptions === "boolean"
        ? previewOptions
        : (previewOptions.displayValueTruncated ?? false);
    const metadata = typeof previewOptions === "boolean" ? undefined : previewOptions.metadata;
    const updated: SmartCell = {
      ...found.cell,
      display_value: displayValue,
      display_value_truncated: displayValueTruncated,
      metadata: metadata !== undefined ? metadata : found.cell.metadata,
      value: value !== undefined ? value : found.cell.value,
    };

    this.rows.get(found.rowIndex)?.set(found.columnId, updated);
    this._clearStreamingBuffer(found.rowIndex, found.columnId);
    this._recordLocalStatusTouch(found.rowIndex, found.columnId);

    this._scheduleDirtyFlush(found.rowIndex, found.columnId);
  };

  /**
   * Update cell content by row/column coordinates.
   *
   * Preferred over `updateCell` for WebSocket events because placeholder
   * cells have synthetic `id` values that do not match server UUIDs, making
   * the `id`-based lookup fail silently.
   */
  updateCellByCoords = (
    rowIndex: number,
    columnId: string,
    options: {
      displayValue: string | null;
      value?: SmartCell["value"];
      displayValueTruncated?: boolean;
      metadata?: SmartCell["metadata"];
      executionId?: string | null;
      updatedAt?: string;
    },
  ): boolean => {
    const found = this._findCellByCoords(rowIndex, columnId);
    if (!found) {
      return false;
    }

    const {
      displayValue,
      value,
      displayValueTruncated = false,
      metadata,
      executionId,
      updatedAt,
    } = options;
    const updated: SmartCell = {
      ...found.cell,
      ...(executionId !== undefined ? { execution_id: executionId } : {}),
      display_value: displayValue,
      display_value_truncated: displayValueTruncated,
      metadata: metadata !== undefined ? metadata : found.cell.metadata,
      value: value !== undefined ? value : found.cell.value,
      updated_at: updatedAt ?? found.cell.updated_at,
    };

    if (!this._shouldAcceptAuthoritativeCell(rowIndex, columnId, updated, found.cell)) {
      return false;
    }

    (this.rows.get(rowIndex) as ObservableMap<string, SmartCell>).set(columnId, updated);
    this._recordCellExecutionOrder(updated);
    this._clearStreamingBuffer(rowIndex, columnId);
    this._recordLocalStatusTouch(rowIndex, columnId);

    this._scheduleDirtyFlush(rowIndex, columnId);
    return true;
  };

  resetVisibleHumanColumnCells = (columnId: string): void => {
    for (const [rowIndex, rowMap] of this.rows.entries()) {
      const existingCell = rowMap.get(columnId);
      if (!existingCell) {
        continue;
      }

      const updated: SmartCell = {
        ...existingCell,
        status: CellStatus.COMPLETED,
        display_value: "",
        value: { value: null },
        input_hash: null,
        last_computed_version: null,
      };

      rowMap.set(columnId, updated);
      this._scheduleDirtyFlush(rowIndex, columnId);
    }
  };

  private _getStreamingCellKey = (rowIndex: number, columnId: string): string =>
    `${rowIndex}\u0000${columnId}`;

  private _getStreamingPreview = (displayValue: string): string =>
    displayValue.length > STREAMING_CELL_PREVIEW_CHAR_LIMIT
      ? displayValue.slice(0, STREAMING_CELL_PREVIEW_CHAR_LIMIT)
      : displayValue;

  private _mergeStreamingValue = (
    currentValue: SmartCell["value"],
    preview: string,
    totalSize: number,
    isTruncated: boolean,
  ): SmartCell["value"] => {
    if (isTruncated) {
      return {
        __truncated: true,
        size: totalSize,
        type: "string",
      };
    }

    if (isObjectValue(currentValue) && typeof currentValue.value === "string") {
      return { ...currentValue, value: preview };
    }

    if (typeof currentValue === "string") {
      return preview;
    }

    return currentValue;
  };

  private _clearStreamingBuffer = (rowIndex: number, columnId: string): void => {
    const key = this._getStreamingCellKey(rowIndex, columnId);
    const buffer = this._streamingDeltaBuffers.get(key);
    if (buffer && buffer.timerId !== null) {
      clearTimeout(buffer.timerId);
    }
    this._streamingDeltaBuffers.delete(key);
  };

  private _flushStreamingCell = (key: string): void => {
    const buffer = this._streamingDeltaBuffers.get(key);
    if (!buffer) {
      return;
    }

    buffer.timerId = null;
    const found = this._findCellByCoords(buffer.rowIndex, buffer.columnId);
    if (!found) {
      this._streamingDeltaBuffers.delete(key);
      return;
    }

    const updated: SmartCell = {
      ...found.cell,
      display_value: buffer.preview,
      value: this._mergeStreamingValue(
        found.cell.value,
        buffer.preview,
        buffer.totalSize,
        buffer.isTruncated,
      ),
    };

    (this.rows.get(buffer.rowIndex) as ObservableMap<string, SmartCell>).set(
      buffer.columnId,
      updated,
    );
    this._scheduleDirtyFlush(buffer.rowIndex, buffer.columnId);
  };

  /**
   * Append a streaming delta chunk to a cell's display_value.
   *
   * Called for `SMART_TABLE_CELL_CONTENT_DELTA` WebSocket events so that
   * LLM tokens stream into a bounded preview in throttled UI batches.
   */
  appendCellDelta = (
    rowIndex: number,
    columnId: string,
    delta: string,
    executionId?: string | null,
  ): void => {
    const found = this._findCellByCoords(rowIndex, columnId);
    if (!found) {
      return;
    }

    const currentOrder = this._getCellExecutionOrder(rowIndex, columnId, found.cell);
    if (
      !PENDING_CELL_STATUSES.has(found.cell.status) ||
      (executionId && currentOrder?.executionId && currentOrder.executionId !== executionId)
    ) {
      return;
    }

    const key = this._getStreamingCellKey(rowIndex, columnId);
    const existingBuffer = this._streamingDeltaBuffers.get(key);
    const initialDisplay = existingBuffer?.preview ?? found.cell.display_value ?? "";
    const totalSize = (existingBuffer?.totalSize ?? initialDisplay.length) + delta.length;
    const preview = this._getStreamingPreview(`${initialDisplay}${delta}`);
    const isTruncated =
      existingBuffer?.isTruncated === true || totalSize > STREAMING_CELL_PREVIEW_CHAR_LIMIT;
    const updated: SmartCell = {
      ...found.cell,
      ...(executionId ? { execution_id: executionId } : {}),
      value: this._mergeStreamingValue(found.cell.value, preview, totalSize, isTruncated),
    };

    (this.rows.get(rowIndex) as ObservableMap<string, SmartCell>).set(columnId, updated);
    this._recordCellExecutionOrder(updated);
    this._recordLocalStatusTouch(rowIndex, columnId);

    if (existingBuffer) {
      existingBuffer.preview = preview;
      existingBuffer.totalSize = totalSize;
      existingBuffer.isTruncated = isTruncated;
      if (existingBuffer.timerId === null) {
        existingBuffer.timerId = setTimeout(() => {
          this._flushStreamingCell(key);
        }, STREAMING_CELL_FLUSH_MS);
      }
      return;
    }

    const timerId = setTimeout(() => {
      this._flushStreamingCell(key);
    }, STREAMING_CELL_FLUSH_MS);

    this._streamingDeltaBuffers.set(key, {
      rowIndex,
      columnId,
      preview,
      totalSize,
      isTruncated,
      timerId,
    });
  };

  /**
   * Merge server response fields after successful mutation.
   */
  reconcileCell = (serverCell: SmartCell): void => {
    const found = this._findCellById(serverCell.id);
    if (!found) {
      return;
    }

    traceSmartTable({
      category: "cell-status",
      op: "reconcileCell",
      source: "sheet-data-store:reconcileCell",
      rowIndex: found.rowIndex,
      columnId: found.columnId,
      from: found.cell.status,
      to: serverCell.status,
    });

    this.rows.get(found.rowIndex)?.set(found.columnId, serverCell);
    this._clearStreamingBuffer(found.rowIndex, found.columnId);
    this._recordLocalStatusTouch(found.rowIndex, found.columnId);

    this._scheduleDirtyFlush(found.rowIndex, found.columnId);
  };

  /**
   * Rollback a cell to its pre-mutation snapshot.
   */
  restoreCellSnapshot = (snapshot: CellSnapshot): void => {
    if (!snapshot.cell || snapshot.rowIndex === -1) {
      return;
    }

    const previousCell = this.rows.get(snapshot.rowIndex)?.get(snapshot.columnId);
    traceSmartTable({
      category: "cell-status",
      op: "restoreCellSnapshot",
      source: "sheet-data-store:restoreCellSnapshot",
      rowIndex: snapshot.rowIndex,
      columnId: snapshot.columnId,
      from: previousCell?.status,
      to: snapshot.cell.status,
    });

    this.rows.get(snapshot.rowIndex)?.set(snapshot.columnId, snapshot.cell);
    this._recordCellExecutionOrder(snapshot.cell);
    this._clearStreamingBuffer(snapshot.rowIndex, snapshot.columnId);
    this._recordLocalStatusTouch(snapshot.rowIndex, snapshot.columnId);

    this._scheduleDirtyFlush(snapshot.rowIndex, snapshot.columnId);
  };

  /**
   * Rollback multiple cells to their pre-mutation snapshots.
   */
  restoreCellSnapshots = (snapshots: CellSnapshot[]): void => {
    for (const snapshot of snapshots) {
      this.restoreCellSnapshot(snapshot);
    }
  };

  bindCellSnapshotsToExecution = (
    snapshots: CellSnapshot[],
    executionId: string | null | undefined,
  ): void => {
    if (!executionId) {
      return;
    }

    for (const snapshot of snapshots) {
      const currentCell = this.rows.get(snapshot.rowIndex)?.get(snapshot.columnId);
      if (!currentCell || !PENDING_CELL_STATUSES.has(currentCell.status)) {
        continue;
      }

      if (currentCell.execution_id && currentCell.execution_id !== executionId) {
        continue;
      }

      const updatedCell: SmartCell = {
        ...currentCell,
        execution_id: executionId,
      };
      this.rows.get(snapshot.rowIndex)?.set(snapshot.columnId, updatedCell);
      this._recordCellExecutionOrder(updatedCell);
    }
  };

  /**
   * Update cell status from WebSocket events — hot path, <1ms.
   * Refreshes only the affected column in the affected row.
   */
  updateCellStatus = (
    cellId: string,
    status: CellStatus,
    options: UpdateCellStatusOptions = {},
  ): boolean => {
    const found = this._findCellById(cellId);
    if (!found) {
      return false;
    }

    const { displayValue, error, executionId, updatedAt, authoritative } = options;
    if (
      this._isStaleOutOfOrderStatusTransition(
        found.rowIndex,
        found.columnId,
        found.cell.status,
        status,
      )
    ) {
      traceSmartTable({
        category: "cell-status",
        op: "updateCellStatus:drop-out-of-order",
        source: "sheet-data-store:updateCellStatus",
        rowIndex: found.rowIndex,
        columnId: found.columnId,
        from: found.cell.status,
        to: status,
      });
      return false;
    }
    const column = this.columns.get(found.columnId);
    const shouldPreserveContent =
      shouldPreserveExistingContentForPendingStatus(status, column) ||
      shouldPreserveExistingContentForStaleStatus({
        status,
        column,
        displayValue,
        value: undefined,
      });

    let nextExecutionId = found.cell.execution_id;
    if (executionId !== undefined) {
      nextExecutionId = executionId;
    } else if (!authoritative && status === CellStatus.QUEUED) {
      nextExecutionId = null;
    }

    const updated: SmartCell = {
      ...found.cell,
      execution_id: nextExecutionId,
      status,
      display_value:
        !shouldPreserveContent && displayValue !== undefined
          ? displayValue
          : found.cell.display_value,
      value: error
        ? { ...(isObjectValue(found.cell.value) ? found.cell.value : {}), error }
        : found.cell.value,
      updated_at: updatedAt ?? (authoritative ? found.cell.updated_at : new Date().toISOString()),
    };

    if (
      authoritative &&
      !this._shouldAcceptAuthoritativeCell(found.rowIndex, found.columnId, updated, found.cell)
    ) {
      return false;
    }

    traceSmartTable({
      category: "cell-status",
      op: "updateCellStatus",
      source: "sheet-data-store:updateCellStatus",
      rowIndex: found.rowIndex,
      columnId: found.columnId,
      from: found.cell.status,
      to: status,
    });

    this.rows.get(found.rowIndex)?.set(found.columnId, updated);
    this._recordCellExecutionOrder(updated);
    this._recordLocalStatusTouch(found.rowIndex, found.columnId);
    const refreshReason =
      updated.display_value === found.cell.display_value &&
      Object.is(updated.value, found.cell.value)
        ? "status"
        : "value";
    this._scheduleDirtyFlush(found.rowIndex, found.columnId, refreshReason);
    return true;
  };

  /**
   * Update cell status by row/column coordinates.
   *
   * Preferred over `updateCellStatus` for WebSocket events because placeholder
   * cells have synthetic `id` values that do not match server UUIDs, making
   * the `id`-based lookup fail silently.
   *
   * When `serverId` is provided and differs from the locally stored synthetic
   * id, the cell record is promoted to the real server-assigned UUID so that
   * future id-based lookups (e.g. rollback) work correctly.
   */
  updateCellStatusByCoords = (
    rowIndex: number,
    columnId: string,
    status: CellStatus,
    opts: {
      displayValue?: string | null;
      value?: SmartCell["value"];
      error?: string | null;
      serverId?: string;
      executionId?: string | null;
      updatedAt?: string;
      authoritative?: boolean;
    } = {},
  ): void => {
    const found = this._findCellByCoords(rowIndex, columnId);
    if (!found) {
      return;
    }

    const { displayValue, value, error, serverId, executionId, updatedAt, authoritative } = opts;
    if (this._isStaleOutOfOrderStatusTransition(rowIndex, columnId, found.cell.status, status)) {
      traceSmartTable({
        category: "cell-status",
        op: "updateCellStatusByCoords:drop-out-of-order",
        source: "sheet-data-store:updateCellStatusByCoords",
        rowIndex,
        columnId,
        from: found.cell.status,
        to: status,
      });
      return;
    }
    const column = this.columns.get(columnId);
    const shouldPreserveContent =
      shouldPreserveExistingContentForPendingStatus(status, column) ||
      shouldPreserveExistingContentForStaleStatus({
        status,
        column,
        displayValue,
        value,
      });

    let nextValue: SmartCell["value"];
    if (error) {
      nextValue = { ...(isObjectValue(found.cell.value) ? found.cell.value : {}), error };
    } else if (!shouldPreserveContent && value !== undefined) {
      nextValue = value;
    } else if (
      !shouldPreserveContent &&
      shouldClearStaleErrorPayload(status, displayValue, value, found.cell.value)
    ) {
      nextValue = null;
    } else {
      nextValue = found.cell.value;
    }

    let nextExecutionId = found.cell.execution_id;
    if (executionId !== undefined) {
      nextExecutionId = executionId;
    } else if (!authoritative && status === CellStatus.QUEUED) {
      nextExecutionId = null;
    }

    const updated: SmartCell = {
      ...found.cell,
      // Promote the synthetic id to the real server-assigned UUID once known.
      ...(serverId ? { id: serverId } : {}),
      execution_id: nextExecutionId,
      status,
      display_value:
        !shouldPreserveContent && displayValue !== undefined
          ? displayValue
          : found.cell.display_value,
      value: nextValue,
      updated_at: updatedAt ?? (authoritative ? found.cell.updated_at : new Date().toISOString()),
    };

    if (
      authoritative &&
      !this._shouldAcceptAuthoritativeCell(rowIndex, columnId, updated, found.cell)
    ) {
      return;
    }

    traceSmartTable({
      category: "cell-status",
      op: "updateCellStatusByCoords",
      source: "sheet-data-store:updateCellStatusByCoords",
      rowIndex,
      columnId,
      from: found.cell.status,
      to: status,
    });

    (this.rows.get(rowIndex) as ObservableMap<string, SmartCell>).set(columnId, updated);
    this._recordCellExecutionOrder(updated);
    this._recordLocalStatusTouch(rowIndex, columnId);
    const refreshReason =
      updated.display_value === found.cell.display_value &&
      Object.is(updated.value, found.cell.value)
        ? "status"
        : "value";
    this._scheduleDirtyFlush(rowIndex, columnId, refreshReason);
  };

  /**
   * Batch-apply multiple cell status updates.
   *
   * All MobX mutations are applied synchronously, then the affected rows are
   * added to the unified dirty-row buffer which flushes once per animation
   * frame.  This means even if the WebSocket delivers one update per message,
   * all messages arriving within the same frame share a single AG Grid
   * transaction.
   */
  batchUpdateCellStatuses = (updates: CellStatusUpdate[]): boolean => {
    const coalescedUpdates = this._coalesceCellStatusUpdates(updates);
    let hasNonresidentFilterMatch = false;
    const explicitUpdatesByRow = new Map<number, Set<string>>();
    for (const update of coalescedUpdates) {
      const explicitColumns = explicitUpdatesByRow.get(update.rowIndex) ?? new Set<string>();
      explicitColumns.add(update.columnId);
      explicitUpdatesByRow.set(update.rowIndex, explicitColumns);
    }

    for (const update of coalescedUpdates) {
      const rowMap = this.rows.get(update.rowIndex) as ObservableMap<string, SmartCell> | undefined;
      const column = this.columns.get(update.columnId);
      if (!rowMap || !column) {
        if (this._activeStatusFilterStatuses.has(update.status)) {
          hasNonresidentFilterMatch = true;
        }
        continue;
      }

      const existingCell = rowMap.get(update.columnId);
      if (!existingCell) {
        const createdCell = createCellFromStatusUpdate({
          rowIndex: update.rowIndex,
          columnId: update.columnId,
          status: update.status,
          displayValue: update.displayValue,
          displayValueTruncated: update.displayValueTruncated,
          value: update.value,
          serverId: update.serverId,
          executionId: update.executionId,
          updatedAt: update.updatedAt,
        });
        if (!this._shouldAcceptAuthoritativeCell(update.rowIndex, update.columnId, createdCell)) {
          continue;
        }

        rowMap.set(update.columnId, createdCell);
        this._recordCellExecutionOrder(createdCell);

        this._recordLocalStatusTouch(update.rowIndex, update.columnId);
        this._recordDirtyColumn(update.rowIndex, update.columnId, "value");
        traceSmartTable({
          category: "cell-status",
          op: "batchUpdateCellStatuses:create",
          source: "sheet-data-store:batchUpdateCellStatuses",
          rowIndex: update.rowIndex,
          columnId: update.columnId,
          to: update.status,
        });
        continue;
      }

      if (
        this._isStaleOutOfOrderStatusTransition(
          update.rowIndex,
          update.columnId,
          existingCell.status,
          update.status,
        )
      ) {
        traceSmartTable({
          category: "cell-status",
          op: "batchUpdateCellStatuses:drop-out-of-order",
          source: "sheet-data-store:batchUpdateCellStatuses",
          rowIndex: update.rowIndex,
          columnId: update.columnId,
          from: existingCell.status,
          to: update.status,
        });
        continue;
      }

      if (TERMINAL_CELL_STATUSES.has(update.status)) {
        this._clearStreamingBuffer(update.rowIndex, update.columnId);
      }

      const { error, value, serverId, displayValue, displayValueTruncated, metadata } = update;
      const shouldPreserveContent =
        shouldPreserveExistingContentForPendingStatus(update.status, column) ||
        shouldPreserveExistingContentForStaleStatus({
          status: update.status,
          column,
          displayValue,
          value,
        });

      let nextValue: SmartCell["value"];
      if (error) {
        nextValue = { ...(isObjectValue(existingCell.value) ? existingCell.value : {}), error };
      } else if (!shouldPreserveContent && value !== undefined) {
        nextValue = value;
      } else if (
        !shouldPreserveContent &&
        shouldClearStaleErrorPayload(update.status, displayValue, value, existingCell.value)
      ) {
        nextValue = null;
      } else {
        nextValue = existingCell.value;
      }

      const nextDisplayValue =
        !shouldPreserveContent && displayValue !== undefined
          ? displayValue
          : existingCell.display_value;
      const nextCellId = serverId || existingCell.id;
      const nextExecutionId =
        update.executionId !== undefined ? update.executionId : existingCell.execution_id;
      const nextUpdatedAt = update.updatedAt ?? existingCell.updated_at;
      const nextDisplayValueTruncated =
        !shouldPreserveContent && displayValueTruncated !== undefined
          ? displayValueTruncated
          : existingCell.display_value_truncated;
      const nextMetadata = metadata !== undefined ? metadata : existingCell.metadata;

      const dirtyColumnsForRow = this._dirtyColumnsByRow.get(update.rowIndex) ?? new Set<string>();
      const explicitColumnsForRow = explicitUpdatesByRow.get(update.rowIndex) ?? new Set<string>();
      const updateStatusIsPending = PENDING_CELL_STATUSES.has(update.status);

      const propagateStatusToExecutionMetadataGroup = (): boolean => {
        let touchedGroupCell = false;

        for (const groupColumnId of getExecutionMetadataGroupColumnIds(
          update.columnId,
          this.columns,
        )) {
          if (groupColumnId === update.columnId || explicitColumnsForRow.has(groupColumnId)) {
            continue;
          }

          const groupCell = rowMap.get(groupColumnId);
          if (!groupCell) {
            continue;
          }

          const shouldPropagateStatus =
            (updateStatusIsPending && !PENDING_CELL_STATUSES.has(groupCell.status)) ||
            (!updateStatusIsPending &&
              (PENDING_CELL_STATUSES.has(groupCell.status) ||
                groupCell.status === CellStatus.STALE));

          if (!shouldPropagateStatus || groupCell.status === update.status) {
            continue;
          }

          const updatedGroupCell: SmartCell = {
            ...groupCell,
            ...(update.executionId !== undefined ? { execution_id: update.executionId } : {}),
            status: update.status,
            updated_at: nextUpdatedAt,
          };
          if (
            !this._shouldAcceptAuthoritativeCell(
              update.rowIndex,
              groupColumnId,
              updatedGroupCell,
              groupCell,
            )
          ) {
            continue;
          }
          rowMap.set(groupColumnId, updatedGroupCell);
          this._recordCellExecutionOrder(updatedGroupCell);
          this._recordLocalStatusTouch(update.rowIndex, groupColumnId);
          this._recordDirtyColumn(update.rowIndex, groupColumnId, "status");
          traceSmartTable({
            category: "cell-status",
            op: "batchUpdateCellStatuses:group-propagate",
            source: "sheet-data-store:batchUpdateCellStatuses",
            rowIndex: update.rowIndex,
            columnId: groupColumnId,
            from: groupCell.status,
            to: update.status,
          });
          dirtyColumnsForRow.add(groupColumnId);
          touchedGroupCell = true;
        }

        return touchedGroupCell;
      };

      const updated: SmartCell = {
        ...existingCell,
        ...(serverId ? { id: serverId } : {}),
        execution_id: nextExecutionId,
        status: update.status,
        display_value: nextDisplayValue,
        display_value_truncated: nextDisplayValueTruncated,
        metadata: nextMetadata,
        value: nextValue,
        updated_at: nextUpdatedAt,
      };

      if (
        !this._shouldAcceptAuthoritativeCell(
          update.rowIndex,
          update.columnId,
          updated,
          existingCell,
        )
      ) {
        continue;
      }

      const primaryCellChanged = !(
        existingCell.id === nextCellId &&
        existingCell.execution_id === nextExecutionId &&
        existingCell.status === update.status &&
        existingCell.display_value === nextDisplayValue &&
        existingCell.display_value_truncated === nextDisplayValueTruncated &&
        Object.is(existingCell.metadata, nextMetadata) &&
        Object.is(existingCell.value, nextValue) &&
        existingCell.updated_at === nextUpdatedAt
      );
      const groupCellChanged = propagateStatusToExecutionMetadataGroup();

      if (!primaryCellChanged && !groupCellChanged) {
        continue;
      }

      if (primaryCellChanged) {
        rowMap.set(update.columnId, updated);
        this._recordCellExecutionOrder(updated);
        this._recordLocalStatusTouch(update.rowIndex, update.columnId);
        const refreshReason =
          nextDisplayValue === existingCell.display_value &&
          Object.is(nextValue, existingCell.value)
            ? "status"
            : "value";
        this._recordDirtyColumn(update.rowIndex, update.columnId, refreshReason);
        traceSmartTable({
          category: "cell-status",
          op: "batchUpdateCellStatuses:primary",
          source: "sheet-data-store:batchUpdateCellStatuses",
          rowIndex: update.rowIndex,
          columnId: update.columnId,
          from: existingCell.status,
          to: update.status,
        });
        dirtyColumnsForRow.add(update.columnId);
      }

      this._dirtyRows.add(update.rowIndex);
      this._dirtyColumnsByRow.set(update.rowIndex, dirtyColumnsForRow);
    }

    // Schedule a single RAF flush (no-ops if one is already scheduled)
    if (this._dirtyRows.size > 0 && this._flushRafId === null) {
      if (this._isGridRefreshSuspended) {
        this.markNeedsRefreshOnActivate();
      } else {
        this._flushRafId = requestAnimationFrame(this._flushDirtyRows);
      }
    }

    return hasNonresidentFilterMatch;
  };

  private _coalesceCellStatusUpdates = (updates: CellStatusUpdate[]): CellStatusUpdate[] => {
    if (updates.length <= 1) {
      return updates;
    }

    const latestByCellKey = new Map<string, CellStatusUpdate>();
    for (const update of updates) {
      latestByCellKey.set(this._localStatusTouchKey(update.rowIndex, update.columnId), update);
    }

    return updates.filter(update => {
      const latest = latestByCellKey.get(
        this._localStatusTouchKey(update.rowIndex, update.columnId),
      );
      if (!latest || latest === update) {
        return true;
      }

      return !(
        PENDING_CELL_STATUSES.has(update.status) && TERMINAL_CELL_STATUSES.has(latest.status)
      );
    });
  };

  /**
   * Insert rows that newly match the active status filter at their
   * row-index-ordered position among the currently loaded rows.
   */
  private _applyStatusFilterAdds = (rowsToAdd: AgGridRowData[]): void => {
    if (!this._gridApi) {
      return;
    }

    const loadedRowIndices: number[] = [];
    this._gridApi.forEachNode(rowNode => {
      const rowIndex = rowNode.data?._rowIndex;
      if (typeof rowIndex === "number") {
        loadedRowIndices.push(rowIndex);
      }
    });
    loadedRowIndices.sort((a, b) => a - b);

    const lowerBound = (target: number): number => {
      let low = 0;
      let high = loadedRowIndices.length;
      while (low < high) {
        const mid = Math.floor((low + high) / 2);
        if (loadedRowIndices[mid] < target) {
          low = mid + 1;
        } else {
          high = mid;
        }
      }
      return low;
    };

    const sortedAdds = [...rowsToAdd].sort((a, b) => a._rowIndex - b._rowIndex);
    let addedSoFar = 0;
    for (const row of sortedAdds) {
      this._gridApi.applyServerSideTransaction({
        add: [row],
        addIndex: lowerBound(row._rowIndex) + addedSoFar,
      });
      addedSoFar += 1;
    }

    this.setKnownTotalRows(this.totalRows + sortedAdds.length);
    traceSmartTableRows({
      category: "grid-op",
      op: "applyServerSideTransaction:add",
      source: "sheet-data-store:_flushDirtyRows",
      rowIndices: sortedAdds.map(row => row._rowIndex),
      detail: { reason: "row-now-matches-status-filter" },
    });
  };

  // ── Unified RAF dirty-row flush ──────────

  /**
   * Mark a row (and optionally a column) as needing an AG Grid transaction.
   * The actual `applyServerSideTransaction` + `refreshCells` is deferred to
   * the next animation frame so that many synchronous mutations within the
   * same tick (e.g. a loop of WS updates, or an authoritative fetch landing
   * right after a status update) collapse into a single grid operation.
   */
  private _recordDirtyColumn = (
    rowIndex: number,
    columnId: string,
    reason: DirtyRefreshReason,
  ): void => {
    this._dirtyRows.add(rowIndex);
    const dirtyColumnsForRow = this._dirtyColumnsByRow.get(rowIndex) ?? new Set<string>();
    dirtyColumnsForRow.add(columnId);
    this._dirtyColumnsByRow.set(rowIndex, dirtyColumnsForRow);

    const reasonsForRow =
      this._dirtyRefreshReasonsByRow.get(rowIndex) ?? new Map<string, DirtyRefreshReason>();
    const existingReason = reasonsForRow.get(columnId);
    reasonsForRow.set(columnId, existingReason === "value" ? "value" : reason);
    this._dirtyRefreshReasonsByRow.set(rowIndex, reasonsForRow);
  };

  private _scheduleDirtyFlush = (
    rowIndex: number,
    columnId?: string,
    reason: DirtyRefreshReason = "value",
  ): void => {
    this._dirtyRows.add(rowIndex);
    if (columnId) {
      this._recordDirtyColumn(rowIndex, columnId, reason);
    }
    if (this._flushRafId === null) {
      if (this._isGridRefreshSuspended) {
        this.markNeedsRefreshOnActivate();
      } else {
        this._flushRafId = requestAnimationFrame(this._flushDirtyRows);
      }
    }
  };

  /**
   * Drain the dirty-row buffer into a single AG Grid transaction + refresh.
   *
   * Called once per animation frame (~16 ms).  This collapses what was
   * previously hundreds of individual `applyServerSideTransaction` calls
   * (one per WS event, one per authoritative fetch, one per streaming
   * token) into at most **one** per frame.
   */
  private _flushDirtyRows = (): void => {
    this._flushRafId = null;

    if (this._isGridRefreshSuspended) {
      this.markNeedsRefreshOnActivate();
      return;
    }

    if (this._dirtyRows.size === 0 || !this._gridApi) {
      this._dirtyRows.clear();
      this._dirtyColumnsByRow.clear();
      this._dirtyRefreshReasonsByRow.clear();
      this._rowsNeedingRedraw.clear();
      return;
    }

    const rowIndices = Array.from(this._dirtyRows);
    const dirtyColumnsByRow = new Map(this._dirtyColumnsByRow);
    const dirtyRefreshReasonsByRow = new Map(this._dirtyRefreshReasonsByRow);
    const rowsNeedingRedraw = new Set(this._rowsNeedingRedraw);
    this._dirtyRows.clear();
    this._dirtyColumnsByRow.clear();
    this._dirtyRefreshReasonsByRow.clear();
    this._rowsNeedingRedraw.clear();

    // Partition dirty rows against the active status filter so the grid's
    // row set atomically tracks row status: rows that stop matching are
    // removed, rows that start matching are inserted at their row-index
    // position, everything else updates in place.
    const hasActiveStatusFilter = this._activeStatusFilterStatuses.size > 0;
    const rowsToUpdate: AgGridRowData[] = [];
    const rowsToRemove: AgGridRowData[] = [];
    const rowsToAdd: AgGridRowData[] = [];

    for (const rowIndex of rowIndices) {
      const rowData = this._buildRowData(rowIndex);
      if (!hasActiveStatusFilter) {
        rowsToUpdate.push(rowData);
        continue;
      }

      const isInGrid = Boolean(this._gridApi.getRowNode(rowData.id));
      const matchesFilter = this._rowMatchesActiveStatusFilter(rowIndex);
      if (isInGrid && matchesFilter) {
        rowsToUpdate.push(rowData);
      } else if (isInGrid && !matchesFilter) {
        rowsToRemove.push(rowData);
      } else if (!isInGrid && matchesFilter) {
        rowsToAdd.push(rowData);
      }
    }

    if (rowsToUpdate.length > 0) {
      // Must stay synchronous: renderers read `_status_*` fields from the row
      // data object, and the `refreshCells` calls below repaint from whatever
      // data the row node holds *right now*. An async transaction would let
      // those repaints run against stale data and then never repaint again
      // once the deferred transaction swaps the data in (SSRM update
      // transactions do not force-refresh cells).
      this._gridApi.applyServerSideTransaction({ update: rowsToUpdate });
      traceSmartTableRows({
        category: "grid-op",
        op: "applyServerSideTransaction:update",
        source: "sheet-data-store:_flushDirtyRows",
        rowIndices: rowsToUpdate.map(row => row._rowIndex),
      });
    }

    if (rowsToRemove.length > 0) {
      this._gridApi.applyServerSideTransaction({ remove: rowsToRemove });
      this.setKnownTotalRows(Math.max(0, this.totalRows - rowsToRemove.length));
      traceSmartTableRows({
        category: "grid-op",
        op: "applyServerSideTransaction:remove",
        source: "sheet-data-store:_flushDirtyRows",
        rowIndices: rowsToRemove.map(row => row._rowIndex),
        detail: { reason: "row-no-longer-matches-status-filter" },
      });
    }

    if (rowsToAdd.length > 0) {
      this._applyStatusFilterAdds(rowsToAdd);
    }

    const rowNodesToRefresh: IRowNode[] = [];
    const valueRefreshRowNodes: IRowNode[] = [];
    const valueColumnsToRefresh = new Set<string>();
    const statusRefreshRowNodes: IRowNode[] = [];
    const statusColumnsToRefresh = new Set<string>();

    for (const rowIndex of rowIndices) {
      const rowNode = this._gridApi.getRowNode(String(rowIndex));
      if (rowNode) {
        rowNodesToRefresh.push(rowNode);
      }

      const dirtyColumns = dirtyColumnsByRow.get(rowIndex);
      if (!rowNode || !dirtyColumns || dirtyColumns.size === 0 || rowsNeedingRedraw.has(rowIndex)) {
        continue;
      }

      const refreshReasons = dirtyRefreshReasonsByRow.get(rowIndex);
      for (const colId of dirtyColumns) {
        const refreshReason = refreshReasons?.get(colId) ?? "value";
        const targetRowNodes =
          refreshReason === "status" ? statusRefreshRowNodes : valueRefreshRowNodes;
        const targetColumns =
          refreshReason === "status" ? statusColumnsToRefresh : valueColumnsToRefresh;
        targetRowNodes.push(rowNode);

        for (const expandedColumnId of getExpandedColumnIdsForRefresh(colId, this.columns)) {
          targetColumns.add(expandedColumnId);
        }
      }
    }

    const rowNodesToRedraw = rowNodesToRefresh.filter(rowNode => {
      const rowIndex = rowNode.data?._rowIndex;
      return typeof rowIndex === "number" && rowsNeedingRedraw.has(rowIndex);
    });

    if (rowNodesToRedraw.length > 0) {
      this._gridApi.redrawRows({ rowNodes: rowNodesToRedraw });
      traceSmartTableRows({
        category: "grid-op",
        op: "redrawRows",
        source: "sheet-data-store:_flushDirtyRows",
        rowIndices: rowNodesToRedraw
          .map(rowNode => rowNode.data?._rowIndex)
          .filter((rowIndex): rowIndex is number => typeof rowIndex === "number"),
      });
    }

    if (valueRefreshRowNodes.length > 0 && valueColumnsToRefresh.size > 0) {
      this._gridApi.refreshCells({
        rowNodes: [...new Set(valueRefreshRowNodes)],
        columns: Array.from(valueColumnsToRefresh),
        suppressFlash: true,
      });
      traceSmartTableRows({
        category: "grid-op",
        op: "refreshCells:value",
        source: "sheet-data-store:_flushDirtyRows",
        rowIndices: [...new Set(valueRefreshRowNodes)]
          .map(rowNode => rowNode.data?._rowIndex)
          .filter((rowIndex): rowIndex is number => typeof rowIndex === "number"),
        detail: { columns: Array.from(valueColumnsToRefresh) },
      });
    }

    if (statusRefreshRowNodes.length > 0 && statusColumnsToRefresh.size > 0) {
      this._gridApi.refreshCells({
        rowNodes: [...new Set(statusRefreshRowNodes)],
        columns: Array.from(statusColumnsToRefresh),
        // Status-only changes update sibling metadata fields read by renderers, not cell values.
        force: true,
        suppressFlash: true,
      });
      traceSmartTableRows({
        category: "grid-op",
        op: "refreshCells:status",
        source: "sheet-data-store:_flushDirtyRows",
        rowIndices: [...new Set(statusRefreshRowNodes)]
          .map(rowNode => rowNode.data?._rowIndex)
          .filter((rowIndex): rowIndex is number => typeof rowIndex === "number"),
        detail: { columns: Array.from(statusColumnsToRefresh) },
      });
    }
  };

  // ── Row mutations ────────────────────────

  /**
   * Insert placeholder rows instantly before network call.
   * Returns row snapshots for rollback.
   *
   * The caller passes the append start from the current backend row count;
   * loaded SSRM blocks are intentionally not used to infer the next index.
   */
  addRowsOptimistic = (startIndex: number, count: number): RowSnapshot[] => {
    const snapshots: RowSnapshot[] = [];

    for (let i = 0; i < count; i += 1) {
      const rowIndex = startIndex + i;
      const cellMap: ObservableMap<string, SmartCell> = observable.map();

      for (const [colId] of this.columns.entries()) {
        cellMap.set(colId, this._buildPlaceholderCell(rowIndex, colId));
      }

      snapshots.push({ rowIndex, cells: {} });
      this.rows.set(rowIndex, cellMap);
      this.pendingCreatedRowIndices.add(rowIndex);
    }

    this.totalRows += count;

    return snapshots;
  };

  /**
   * Replace temporary row placeholders with real server-assigned cells.
   */
  reconcileAddedRows = (serverRows: GridRow[]): void => {
    this.reconcileCreatedRows(serverRows);
  };

  reconcileCreatedRows = (serverRows: GridRow[]): number[] => {
    let addedRowCount = 0;
    const touchedRowIndices: number[] = [];

    for (const row of serverRows) {
      const hadExistingRow = this.rows.has(row.row_index);
      const cellMap: ObservableMap<string, SmartCell> = observable.map();

      for (const [colId, cell] of Object.entries(row.cells)) {
        cellMap.set(colId, cell);
      }

      this.rows.set(row.row_index, cellMap);
      this.pendingCreatedRowIndices.delete(row.row_index);
      this.pendingHydrationRowIndices.delete(row.row_index);
      touchedRowIndices.push(row.row_index);

      if (!hadExistingRow) {
        addedRowCount += 1;
      }
    }

    this.totalRows += addedRowCount;

    return touchedRowIndices;
  };

  reconcileCreatedColumnCells = (columnId: string, cells: SmartCell[]): number[] => {
    const touchedRowIndices = new Set<number>();

    for (const cell of cells) {
      const row = this.rows.get(cell.row_index);
      if (!row) {
        continue;
      }

      row.set(columnId, cell);
      touchedRowIndices.add(cell.row_index);
    }

    this.pendingHydrationColumnIds.delete(columnId);

    return [...touchedRowIndices];
  };

  replaceCompositionColumnWithConvertedTextColumns = (
    parentColumnId: string,
    columns: SmartColumn[],
    cells: SmartCell[],
  ): number[] => {
    this.pendingCreatedColumns.delete(parentColumnId);
    this.pendingHydrationColumnIds.delete(parentColumnId);
    this.pendingDeletedColumnIds.add(parentColumnId);

    this.columns.delete(parentColumnId);

    const createdColumnIds = new Set(columns.map(column => column.id));
    for (const column of columns) {
      this.pendingDeletedColumnIds.delete(column.id);
      this.pendingHydrationColumnIds.delete(column.id);
      this.columns.set(column.id, column);
    }

    const touchedRowIndices = new Set<number>();

    for (const [rowIndex, rowMap] of this.rows.entries()) {
      if (rowMap.has(parentColumnId)) {
        rowMap.delete(parentColumnId);
        touchedRowIndices.add(rowIndex);
      }
    }

    for (const cell of cells) {
      const rowMap = this.rows.get(cell.row_index);
      if (!rowMap) {
        continue;
      }

      if (!createdColumnIds.has(cell.smart_column_id)) {
        continue;
      }

      rowMap.set(cell.smart_column_id, cell);
      touchedRowIndices.add(cell.row_index);
    }

    this._refreshCellsForColumns([...createdColumnIds], [...touchedRowIndices]);

    return [...touchedRowIndices];
  };

  /**
   * Instantly remove rows from the local store.
   * Returns snapshots for rollback.
   */
  removeRows = (indices: number[]): RowSnapshot[] => {
    const snapshots: RowSnapshot[] = [];
    const rowsToRemove: AgGridRowData[] = [];

    for (const index of indices) {
      const cellMap = this.rows.get(index);
      if (cellMap) {
        const cells: Record<string, SmartCell> = {};
        for (const [colId, cell] of cellMap.entries()) {
          cells[colId] = cell;
        }
        snapshots.push({ rowIndex: index, cells });
        rowsToRemove.push(this._buildRowData(index));
        this.rows.delete(index);
        this.pendingCreatedRowIndices.delete(index);
        this.pendingHydrationRowIndices.delete(index);
      }
    }

    this.totalRows = Math.max(0, this.totalRows - snapshots.length);

    if (this._gridApi && rowsToRemove.length > 0) {
      this._gridApi.applyServerSideTransaction({ remove: rowsToRemove });
    }

    return snapshots;
  };

  /**
   * Put rows back (e.g. if a deletion failed).
   * Note: This does not restore original position exactly, but the server
   * assigns indices anyway so next hydration will sort it.
   */
  restoreRows = (snapshots: RowSnapshot[]): void => {
    const rowsToAdd: AgGridRowData[] = [];

    for (const { rowIndex, cells } of snapshots) {
      const cellMap: ObservableMap<string, SmartCell> = observable.map();
      for (const [colId, cell] of Object.entries(cells)) {
        cellMap.set(colId, cell);
      }

      this.rows.set(rowIndex, cellMap);
      rowsToAdd.push(this._buildRowData(rowIndex));
    }

    this.totalRows += snapshots.length;

    if (this._gridApi && rowsToAdd.length > 0) {
      this._gridApi.applyServerSideTransaction({ add: rowsToAdd });
    }
  };

  // ── Column mutations ─────────────────────

  /**
   * Insert a new column optimistically.
   * Returns a snapshot for rollback.
   *
   * Structural column changes are pushed to AG Grid via `setGridOption("columnDefs", ...)`.
   * Existing rows are refreshed only for the new column.
   */
  addColumnOptimistic = (col: SmartColumn): ColumnSnapshot => {
    const snapshot: ColumnSnapshot = {
      columnId: col.id,
      column: null,
      position: col.position_rank,
    };

    this.pendingCreatedColumns.set(col.id, {
      tempId: col.id,
      title: col.title,
      positionRank: col.position_rank,
      column: col,
    });
    this.pendingHydrationColumnIds.add(col.id);
    this.columns.set(col.id, col);

    for (const [rowIndex, cellMap] of this.rows.entries()) {
      cellMap.set(col.id, this._buildPlaceholderCell(rowIndex, col.id));
    }

    this._refreshCellsForColumn(col.id);

    return snapshot;
  };

  addDuplicateColumnsOptimistic = action(
    (specs: OptimisticDuplicateColumnSpec[]): ColumnSnapshot[] => {
      const snapshots: ColumnSnapshot[] = [];
      const createdColumnIds: string[] = [];

      for (const { column, sourceColumnId } of specs) {
        snapshots.push({
          columnId: column.id,
          column: null,
          position: column.position_rank,
        });

        this.pendingCreatedColumns.set(column.id, {
          tempId: column.id,
          title: column.title,
          positionRank: column.position_rank,
          column,
          sourceColumnId,
        });
        this.pendingHydrationColumnIds.delete(column.id);
        this.columns.set(column.id, column);
        createdColumnIds.push(column.id);
      }

      for (const [rowIndex, cellMap] of this.rows.entries()) {
        for (const { column, sourceColumnId } of specs) {
          const sourceCell = cellMap.get(sourceColumnId);
          cellMap.set(
            column.id,
            sourceCell
              ? this._cloneCellForColumn(sourceCell, column.id)
              : this._buildPlaceholderCell(rowIndex, column.id),
          );
        }
      }

      this._refreshCellsForColumns(createdColumnIds);

      return snapshots;
    },
  );

  private _reconcileColumnRecord = (serverColumn: SmartColumn, tempId?: string): void => {
    if (tempId) {
      this.pendingCreatedColumns.delete(tempId);

      if (this.pendingHydrationColumnIds.has(tempId)) {
        this.pendingHydrationColumnIds.delete(tempId);
        this.pendingHydrationColumnIds.add(serverColumn.id);
      }
    }

    if (tempId && tempId !== serverColumn.id) {
      const temp = this.columns.get(tempId);
      if (temp) {
        this.columns.delete(tempId);

        for (const cellMap of this.rows.values()) {
          const tempCell = cellMap.get(tempId);
          if (tempCell) {
            cellMap.delete(tempId);
            cellMap.set(serverColumn.id, {
              ...tempCell,
              id: `${tempCell.row_index}-${serverColumn.id}`,
              smart_column_id: serverColumn.id,
            });
          }
        }
      }
    }

    this.columns.set(serverColumn.id, serverColumn);
  };

  /**
   * Replace a temporary column with the server-assigned one.
   */
  reconcileColumn = (serverColumn: SmartColumn, tempId?: string): void => {
    this._reconcileColumnRecord(serverColumn, tempId);

    // Push the remapped cell data into SSRM row nodes so the grid
    // renders the correct status (e.g. COMPLETED for TEXT columns)
    // instead of falling back to STALE.
    this._refreshCellsForColumn(serverColumn.id);
  };

  reconcileColumns = action((specs: ColumnReconciliationSpec[]): void => {
    const reconciledColumnIds: string[] = [];

    for (const { column, tempId } of specs) {
      this._reconcileColumnRecord(column, tempId);
      reconciledColumnIds.push(column.id);
    }

    this._refreshCellsForColumns(reconciledColumnIds);
  });

  getColumnAndExecutionMetadataChildIds = (columnId: string): string[] =>
    getExecutionMetadataGroupColumnIds(columnId, this.columns);

  /**
   * Instantly remove a column and its cells.
   * Returns snapshot for rollback.
   */
  removeColumn = (columnId: string): ColumnSnapshot => {
    const col = this.columns.get(columnId);
    const cells: Record<number, SmartCell> = {};

    for (const [rowIndex, cellMap] of this.rows.entries()) {
      const cell = cellMap.get(columnId);
      if (cell) {
        cells[rowIndex] = cell;
      }
    }

    const snapshot: ColumnSnapshot = {
      columnId,
      column: col ? { ...col } : null,
      position: col?.position_rank ?? 0,
      cells,
    };

    this.pendingDeletedColumnIds.add(columnId);
    this.columns.delete(columnId);

    for (const cellMap of this.rows.values()) {
      cellMap.delete(columnId);
    }

    return snapshot;
  };

  /**
   * Batch remove multiple columns atomically to avoid triggering
   * multiple MobX reactions and re-renders.
   */
  removeColumns = action((columnIds: string[]): ColumnSnapshot[] => {
    const snapshots: ColumnSnapshot[] = [];

    for (const columnId of columnIds) {
      const col = this.columns.get(columnId);
      const cells: Record<number, SmartCell> = {};

      for (const [rowIndex, cellMap] of this.rows.entries()) {
        const cell = cellMap.get(columnId);
        if (cell) {
          cells[rowIndex] = cell;
        }
      }

      snapshots.push({
        columnId,
        column: col ? { ...col } : null,
        position: col?.position_rank ?? 0,
        cells,
      });

      this.pendingDeletedColumnIds.add(columnId);
      this.columns.delete(columnId);

      for (const cellMap of this.rows.values()) {
        cellMap.delete(columnId);
      }
    }

    return snapshots;
  });

  /**
   * Capture a snapshot of a column before mutation (for rollback).
   */
  getColumnSnapshot = (columnId: string): ColumnUpdateSnapshot => {
    const column = this.columns.get(columnId);
    return {
      columnId,
      column: column ? { ...column } : null,
    };
  };

  /**
   * Optimistically update a column before the network call.
   *
   * When the column type changes between TEXT and a computed type, placeholder
   * cells (those that have never been run, identified by a null `input_hash`)
   * have their status refreshed to match the new type:
   *   - TEXT → computed : COMPLETED → STALE  (so the "Run" overlay appears)
   *   - computed → TEXT : STALE → COMPLETED  (so the "Run" overlay disappears)
   */
  updateColumn = (
    columnId: string,
    updates: Partial<Pick<SmartColumn, "title" | "type" | "config" | "is_output_column">>,
  ): void => {
    const col = this.columns.get(columnId);
    if (!col) {
      return;
    }

    this.columns.set(columnId, {
      ...col,
      ...updates,
    });

    // If the type is changing, refresh cell statuses and values so that
    // `staleRowIndices` (and therefore the RowNumberHeader badge) reflects the new type.
    if (updates.type === undefined || updates.type === col.type) {
      return;
    }

    const wasText = isPlainTextEditableColumnType(col.type);
    const isNowText = isPlainTextEditableColumnType(updates.type);

    if (wasText === isNowText) {
      return;
    }

    let touched = false;

    if (wasText && !isNowText) {
      // TEXT/TRACE → computed: mark as STALE so the row shows up as unrun.
      // Preserve the current text payload so the UI keeps showing the user's
      // existing value while the newly-smart column is still uncomputed.
      for (const cellMap of this.rows.values()) {
        const cell = (cellMap as ObservableMap<string, SmartCell>).get(columnId);
        if (!cell || cell.input_hash !== null) {
          continue;
        }

        cellMap.set(columnId, {
          ...cell,
          status: CellStatus.STALE,
        });
        touched = true;
      }
    }

    if (!wasText && isNowText) {
      // computed → TEXT: persist displayed value and mark as COMPLETED
      for (const cellMap of this.rows.values()) {
        const cell = (cellMap as ObservableMap<string, SmartCell>).get(columnId);
        if (!cell) {
          continue;
        }

        const { valueToDisplay } = resolveCellDisplayState(cell);
        const finalDisplayValue =
          valueToDisplay === null || valueToDisplay === undefined ? "" : String(valueToDisplay);

        cellMap.set(columnId, {
          ...cell,
          status: CellStatus.COMPLETED,
          display_value: finalDisplayValue,
          value: { text: finalDisplayValue },
          input_hash: null,
          last_computed_version: null,
        });
        touched = true;
      }
    }

    if (touched) {
      this._refreshCellsForColumn(columnId);
    }
  };

  /**
   * Merge a successful server response for an existing column.
   * When the column type changed, delegates to `updateColumn` first so
   * that TEXT↔computed cell status transitions are applied.
   */
  reconcileUpdatedColumn = (serverColumn: SmartColumn): void => {
    const existing = this.columns.get(serverColumn.id);
    if (!existing) {
      this.columns.set(serverColumn.id, serverColumn);
      return;
    }

    if (areSmartColumnsEquivalent(existing, serverColumn)) {
      return;
    }

    if (existing.type !== serverColumn.type) {
      this.updateColumn(serverColumn.id, { type: serverColumn.type });
    }

    const currentColumn = this.columns.get(serverColumn.id);
    const reconciledColumn = {
      ...currentColumn,
      ...serverColumn,
    };

    if (!currentColumn || !areSmartColumnsEquivalent(currentColumn, reconciledColumn)) {
      this.columns.set(serverColumn.id, reconciledColumn);
    }

    const config = serverColumn.config as { pending_removed_sources?: unknown } | undefined;
    const hasPendingRemovedSources =
      Array.isArray(config?.pending_removed_sources) && config.pending_removed_sources.length > 0;

    if (hasPendingRemovedSources) {
      this.markColumnCellsStale(serverColumn.id);
    }
  };

  /**
   * Replace local column metadata with the authoritative server list.
   * Used when Eval SDK (or another client) renames/adds columns while this
   * sheet is open — layout WS alone previously only refreshed row blocks.
   */
  reconcileColumnsFromServer = (serverColumns: SmartColumn[]): boolean => {
    let changed = false;
    const serverIds = new Set<string>();

    for (const serverColumn of serverColumns) {
      if (this.pendingDeletedColumnIds.has(serverColumn.id)) {
        continue;
      }

      serverIds.add(serverColumn.id);
      const existing = this.columns.get(serverColumn.id);
      if (!existing || !areSmartColumnsEquivalent(existing, serverColumn)) {
        changed = true;
      }
      this.reconcileUpdatedColumn(serverColumn);
    }

    for (const columnId of Array.from(this.columns.keys()) as string[]) {
      if (serverIds.has(columnId)) {
        continue;
      }
      if (this.pendingCreatedColumns.has(columnId)) {
        continue;
      }
      // Drop stale server columns that vanished (e.g. renamed away elsewhere).
      this.columns.delete(columnId);
      this.pendingHydrationColumnIds.delete(columnId);
      changed = true;
    }

    return changed;
  };

  /**
   * Update a column's position_rank for drag-and-drop reorder.
   */
  moveColumn = (columnId: string, newRank: number): void => {
    const col = this.columns.get(columnId);
    if (!col) {
      return;
    }

    this.columns.set(columnId, { ...col, position_rank: newRank });
  };

  /**
   * Restore a column from rollback snapshot.
   */
  restoreColumn = (snapshot: ColumnSnapshot | ColumnUpdateSnapshot): void => {
    this.pendingCreatedColumns.delete(snapshot.columnId);
    this.pendingHydrationColumnIds.delete(snapshot.columnId);

    if (!snapshot.column) {
      this.columns.delete(snapshot.columnId);
      for (const cellMap of this.rows.values()) {
        cellMap.delete(snapshot.columnId);
      }
      return;
    }

    this.pendingDeletedColumnIds.delete(snapshot.columnId);
    this.columns.set(snapshot.columnId, snapshot.column);

    if ("position" in snapshot) {
      for (const [rowIndex, cellMap] of this.rows.entries()) {
        if (!cellMap.has(snapshot.columnId)) {
          const restoredCell = snapshot.cells?.[rowIndex];
          if (restoredCell) {
            cellMap.set(snapshot.columnId, restoredCell);
            continue;
          }

          cellMap.set(snapshot.columnId, this._buildPlaceholderCell(rowIndex, snapshot.columnId));
        }
      }
    }
  };

  /**
   * Update the stale count from a WebSocket sheet_change event.
   * Marks terminal non-text cells as STALE up to the reported count.
   *
   * When `affectedColumnIds` is provided only those columns are considered,
   * which avoids incorrectly marking cells in unrelated columns as STALE.
   */
  updateStaleCount = (staleCount: number, affectedColumnIds?: string[]): void => {
    const touchedColumns = new Set<string>();
    let markedStale = 0;

    const columnFilter = affectedColumnIds ? new Set(affectedColumnIds) : null;

    outer: for (const cellMap of this.rows.values()) {
      for (const [colId, cell] of cellMap.entries()) {
        if (markedStale >= staleCount) {
          break outer;
        }
        if (columnFilter && !columnFilter.has(colId)) {
          continue;
        }
        if (
          (cell.status === CellStatus.COMPLETED || cell.status === CellStatus.FAILED) &&
          !isStalenessExemptColumn(this.columns.get(colId), this.columns)
        ) {
          cellMap.set(colId, { ...cell, status: CellStatus.STALE });
          touchedColumns.add(colId);
          markedStale += 1;
        }
      }
    }

    this._refreshCellsForColumns([...touchedColumns]);
  };

  private _markSingleColumnCellsStale = (columnId: string, rowIndices?: number[]): boolean => {
    const col = this.columns.get(columnId);
    if (!col || isStalenessExemptColumn(col, this.columns)) {
      return false;
    }

    const config = col.config as { pending_removed_sources?: unknown } | undefined;
    const hasPendingRemovedSources =
      Array.isArray(config?.pending_removed_sources) && config.pending_removed_sources.length > 0;

    const rowFilter = rowIndices ? new Set(rowIndices) : null;
    const shouldIncludePlaceholders = rowFilter === null;
    let touched = false;

    for (const [rowIndex, cellMap] of this.rows.entries()) {
      if (rowFilter && !rowFilter.has(rowIndex)) {
        continue;
      }
      const cell = (cellMap as ObservableMap<string, SmartCell>).get(columnId);
      if (!cell) {
        continue;
      }

      const hasConcreteDisplayValue =
        cell.display_value !== null &&
        cell.display_value !== undefined &&
        cell.display_value !== "";
      const hasConcreteValue =
        cell.value !== null &&
        cell.value !== undefined &&
        !(
          typeof cell.value === "object" &&
          cell.value !== null &&
          "value" in cell.value &&
          (cell.value as { value?: unknown }).value === ""
        );
      const isPlaceholder =
        cell.input_hash === null && !hasConcreteDisplayValue && !hasConcreteValue;

      const shouldMarkStale =
        cell.status === CellStatus.COMPLETED ||
        cell.status === CellStatus.FAILED ||
        (hasPendingRemovedSources &&
          cell.status !== CellStatus.STALE &&
          !PENDING_CELL_STATUSES.has(cell.status)) ||
        (shouldIncludePlaceholders && isPlaceholder && cell.status !== CellStatus.STALE);

      if (shouldMarkStale) {
        cellMap.set(columnId, {
          ...cell,
          status: CellStatus.STALE,
          ...(isPlaceholder ? { display_value: null, value: null } : {}),
        });
        traceSmartTable({
          category: "cell-status",
          op: "markSingleColumnCellsStale",
          source: "sheet-data-store:_markSingleColumnCellsStale",
          rowIndex,
          columnId,
          from: cell.status,
          to: CellStatus.STALE,
          detail: { isPlaceholder, hasPendingRemovedSources },
        });
        touched = true;
      }
    }

    return touched;
  };

  /**
   * Transition cells to STALE for a set of columns, including grouped and
   * dependency-derived downstream columns.
   */
  markColumnsCellsStale = (columnIds: string[], rowIndices?: number[]): void => {
    if (columnIds.length === 0) {
      return;
    }

    const affectedColumnIds = getColumnsAffectedByStaleness(columnIds, this.columns);
    const touchedColumnIds: string[] = [];

    for (const affectedColumnId of affectedColumnIds) {
      if (this._markSingleColumnCellsStale(affectedColumnId, rowIndices)) {
        touchedColumnIds.push(affectedColumnId);
      }
    }

    if (touchedColumnIds.length > 0) {
      this._refreshCellsForColumns(touchedColumnIds, rowIndices);
    }
  };

  /**
   * Transition cells to STALE for a column.
   *
   * When `rowIndices` is omitted, placeholder cells (never computed, `input_hash === null`)
   * are also marked STALE so newly-computed columns immediately show as unrun.
   */
  markColumnCellsStale = (columnId: string, rowIndices?: number[]): void => {
    this.markColumnsCellsStale([columnId], rowIndices);
  };

  private _isOptimisticallyQueueableColumn = (column: SmartColumn | undefined): boolean =>
    Boolean(column && isRunnableColumnType(column.type));

  private _getOptimisticRunStatusColumnIds = (columnId: string): string[] => {
    const groupColumnIds = getExecutionMetadataGroupColumnIds(columnId, this.columns).filter(
      groupColumnId => this.columns.has(groupColumnId),
    );

    const hasExecutableColumn = groupColumnIds.some(groupColumnId =>
      this._isOptimisticallyQueueableColumn(this.columns.get(groupColumnId)),
    );

    return hasExecutableColumn ? groupColumnIds : [];
  };

  private _getOptimisticRunStatusColumnGroups = (columnIds: string[]): string[][] => {
    const groupsByKey = new Map<string, string[]>();

    for (const columnId of columnIds) {
      const groupColumnIds = this._getOptimisticRunStatusColumnIds(columnId);
      if (groupColumnIds.length === 0) {
        continue;
      }

      groupsByKey.set(groupColumnIds.join("\u0000"), groupColumnIds);
    }

    return Array.from(groupsByKey.values());
  };

  private _finalizeOptimisticRunColumns = (columnGroups: string[][]): void => {
    const executableColumnIds = new Set<string>();

    for (const columnGroup of columnGroups) {
      for (const columnId of columnGroup) {
        if (this._isOptimisticallyQueueableColumn(this.columns.get(columnId))) {
          executableColumnIds.add(columnId);
        }
      }
    }

    for (const columnId of executableColumnIds) {
      const column = this.columns.get(columnId);
      if (!column) {
        continue;
      }

      const finalizedColumn = finalizePendingRemovedCompositionSources(column);
      if (finalizedColumn) {
        this.columns.set(columnId, finalizedColumn);
      }
    }
  };

  private _isEligibleForOptimisticQueue = (
    cell: SmartCell,
    eligibleStatuses: Set<CellStatus> | null,
  ): boolean => {
    if (PENDING_CELL_STATUSES.has(cell.status)) {
      return false;
    }

    return eligibleStatuses === null || eligibleStatuses.has(cell.status);
  };

  private _queueOptimisticRunGroup = ({
    rowIndex,
    cellMap,
    columnIds,
    snapshots,
    snapshotKeys,
    dirtyColumnsForRow,
    eligibleStatuses,
  }: {
    rowIndex: number;
    cellMap: ObservableMap<string, SmartCell>;
    columnIds: string[];
    snapshots: CellSnapshot[];
    snapshotKeys: Set<string>;
    dirtyColumnsForRow: Set<string>;
    eligibleStatuses: Set<CellStatus> | null;
  }): boolean => {
    const shouldQueueGroup = columnIds.some(columnId => {
      const cell = cellMap.get(columnId);
      return cell ? this._isEligibleForOptimisticQueue(cell, eligibleStatuses) : false;
    });

    if (!shouldQueueGroup) {
      return false;
    }

    let touched = false;

    for (const columnId of columnIds) {
      const cell = cellMap.get(columnId);
      if (!cell || PENDING_CELL_STATUSES.has(cell.status)) {
        continue;
      }

      const snapshotKey = `${rowIndex}:${columnId}`;
      if (!snapshotKeys.has(snapshotKey)) {
        snapshots.push({
          cellId: cell.id,
          cell: { ...cell },
          rowIndex,
          columnId,
        });
        snapshotKeys.add(snapshotKey);
      }

      const queuedCell: SmartCell = {
        ...cell,
        execution_id: null,
        status: CellStatus.QUEUED,
        updated_at: new Date().toISOString(),
      };
      cellMap.set(columnId, queuedCell);
      this._recordCellExecutionOrder(queuedCell);
      this._recordLocalStatusTouch(rowIndex, columnId);
      traceSmartTable({
        category: "cell-status",
        op: "queueOptimisticRunGroup",
        source: "sheet-data-store:_queueOptimisticRunGroup",
        rowIndex,
        columnId,
        from: cell.status,
        to: CellStatus.QUEUED,
      });
      dirtyColumnsForRow.add(columnId);
      touched = true;
    }

    return touched;
  };

  /**
   * Optimistically mark executable cells in the given rows as QUEUED.
   * Call this immediately when the user clicks "Run" so the pulsing animation
   * starts before the server responds.
   *
   * `statuses` restricts which cell statuses are transitioned. Defaults to
   * `[STALE]` so COMPLETED cells are never silently re-queued. Pass an
   * empty array to transition every cell regardless of status (matches the
   * backend convention).
   */
  markRowsQueued = (rowIndices: number[], opts?: { statuses?: CellStatus[] }): CellSnapshot[] => {
    if (rowIndices.length === 0) {
      return [];
    }

    const computedColumnIds: string[] = [];
    for (const [colId, col] of this.columns.entries()) {
      if (this._isOptimisticallyQueueableColumn(col)) {
        computedColumnIds.push(colId);
      }
    }

    const columnGroups = this._getOptimisticRunStatusColumnGroups(computedColumnIds);
    if (columnGroups.length === 0) {
      return [];
    }

    this._finalizeOptimisticRunColumns(columnGroups);

    const requestedStatuses = opts?.statuses ?? [CellStatus.STALE];
    const eligibleStatuses: Set<CellStatus> | null =
      requestedStatuses.length === 0 ? null : new Set(requestedStatuses);

    const snapshots: CellSnapshot[] = [];
    const snapshotKeys = new Set<string>();

    for (const rowIndex of rowIndices) {
      const cellMap = this.rows.get(rowIndex) as ObservableMap<string, SmartCell> | undefined;
      if (!cellMap) {
        continue;
      }

      let rowTouched = false;
      const dirtyColumnsForRow = this._dirtyColumnsByRow.get(rowIndex) ?? new Set<string>();

      for (const columnGroup of columnGroups) {
        rowTouched =
          this._queueOptimisticRunGroup({
            rowIndex,
            cellMap,
            columnIds: columnGroup,
            snapshots,
            snapshotKeys,
            dirtyColumnsForRow,
            eligibleStatuses,
          }) || rowTouched;
      }

      if (rowTouched) {
        for (const columnId of dirtyColumnsForRow) {
          this._recordDirtyColumn(rowIndex, columnId, "status");
        }
      }
    }

    if (this._dirtyRows.size > 0 && this._flushRafId === null) {
      this._flushRafId = requestAnimationFrame(this._flushDirtyRows);
    }

    return snapshots;
  };

  /**
   * Optimistically mark exact cells as QUEUED.
   * Used for sparse AG Grid cell selections so a range like A1, C3 does not
   * widen into every selected row/column intersection.
   */
  markCellsQueued = (cellIds: string[]): CellSnapshot[] => {
    if (cellIds.length === 0) {
      return [];
    }

    const targetCellIds = new Set(cellIds);
    const snapshots: CellSnapshot[] = [];
    const snapshotKeys = new Set<string>();

    for (const [rowIndex, cellMap] of this.rows.entries()) {
      let rowTouched = false;
      const dirtyColumnsForRow = this._dirtyColumnsByRow.get(rowIndex) ?? new Set<string>();
      const processedGroupKeys = new Set<string>();

      for (const [columnId, cell] of cellMap.entries()) {
        if (!targetCellIds.has(cell.id)) {
          continue;
        }

        const columnGroup = this._getOptimisticRunStatusColumnIds(columnId);
        if (columnGroup.length === 0) {
          continue;
        }

        const groupKey = columnGroup.join("\u0000");
        if (processedGroupKeys.has(groupKey)) {
          continue;
        }
        processedGroupKeys.add(groupKey);

        rowTouched =
          this._queueOptimisticRunGroup({
            rowIndex,
            cellMap: cellMap as ObservableMap<string, SmartCell>,
            columnIds: columnGroup,
            snapshots,
            snapshotKeys,
            dirtyColumnsForRow,
            eligibleStatuses: null,
          }) || rowTouched;
      }

      if (rowTouched) {
        for (const columnId of dirtyColumnsForRow) {
          this._recordDirtyColumn(rowIndex, columnId, "status");
        }
      }
    }

    if (this._dirtyRows.size > 0 && this._flushRafId === null) {
      this._flushRafId = requestAnimationFrame(this._flushDirtyRows);
    }

    return snapshots;
  };

  /**
   * Optimistically mark all cells in the given columns as QUEUED (across all rows).
   * Skips non-executable columns and cells already in a pending state.
   */
  markColumnsQueued = (columnIds: string[]): CellSnapshot[] => {
    if (columnIds.length === 0) {
      return [];
    }

    const columnGroups = this._getOptimisticRunStatusColumnGroups(columnIds);
    if (columnGroups.length === 0) {
      return [];
    }

    this._finalizeOptimisticRunColumns(columnGroups);

    const snapshots: CellSnapshot[] = [];
    const snapshotKeys = new Set<string>();

    for (const [rowIndex, cellMap] of this.rows.entries()) {
      let rowTouched = false;
      const dirtyColumnsForRow = this._dirtyColumnsByRow.get(rowIndex) ?? new Set<string>();

      for (const columnGroup of columnGroups) {
        rowTouched =
          this._queueOptimisticRunGroup({
            rowIndex,
            cellMap: cellMap as ObservableMap<string, SmartCell>,
            columnIds: columnGroup,
            snapshots,
            snapshotKeys,
            dirtyColumnsForRow,
            eligibleStatuses: null,
          }) || rowTouched;
      }

      if (rowTouched) {
        for (const columnId of dirtyColumnsForRow) {
          this._recordDirtyColumn(rowIndex, columnId, "status");
        }
      }
    }

    if (this._dirtyRows.size > 0 && this._flushRafId === null) {
      this._flushRafId = requestAnimationFrame(this._flushDirtyRows);
    }

    return snapshots;
  };

  // ── Execution state derivation ───────────

  private _resolveStorageColumnId = (columnId: string): string => {
    if (this.columns.has(columnId)) {
      return columnId;
    }

    const baseColumnId = columnId.split("_")[0];
    if (this.columns.has(baseColumnId)) {
      return baseColumnId;
    }

    return columnId;
  };

  isColumnExecutionPending = (columnId: string): boolean => {
    const storageColumnId = this._resolveStorageColumnId(columnId);

    for (const rowCells of this.rows.values()) {
      const cell = (rowCells as ObservableMap<string, SmartCell>).get(storageColumnId);
      if (cell && PENDING_CELL_STATUSES.has(cell.status)) {
        return true;
      }
    }

    return false;
  };

  isCellExecutionPending = (rowIndex: number, columnId: string): boolean => {
    const storageColumnId = this._resolveStorageColumnId(columnId);
    const cell = this.rows.get(rowIndex)?.get(storageColumnId);
    if (!cell) {
      return false;
    }

    return PENDING_CELL_STATUSES.has(cell.status);
  };

  // ── Computed ─────────────────────────────

  /**
   * Columns sorted by position_rank ascending.
   */
  get sortedColumns(): SmartColumn[] {
    return (Array.from(this.columns.values()) as SmartColumn[]).sort(
      (a, b) => a.position_rank - b.position_rank,
    );
  }

  /**
   * AG Grid column definitions derived from sortedColumns.
   * Intended for initial render and imperative column syncing.
   */
  get agGridColumnDefs(): (
    | {
        colId: string;
        field: string;
        valueFormatter?: AgGridValueFormatter;
        headerName: string;
        editable: boolean;
        context: {
          columnType: string;
          configColumnId?: string;
          metricKey?: string;
        };
      }
    | {
        groupId: string;
        headerName: string;
        marryChildren?: boolean;
        openByDefault?: boolean;
        children: {
          colId: string;
          field: string;
          valueGetter?: (params: { data?: AgGridRowData }) => unknown;
          valueFormatter?: AgGridValueFormatter;
          headerName: string;
          editable: boolean;
          context: {
            columnType: string;
            parentColumnId?: string;
            compositionSource?: string;
            configColumnId?: string;
            metricKey?: string;
            pinGroupColIds?: string[];
            pinGroupLabel?: string;
          };
          columnGroupShow?: string;
        }[];
      }
  )[] {
    interface BaseLeafColumnDef {
      colId: string;
      field: string;
      valueGetter?: (params: { data?: AgGridRowData }) => unknown;
      valueFormatter?: AgGridValueFormatter;
      headerName: string;
      editable: boolean;
      context: {
        columnType: string;
        parentColumnId?: string;
        compositionSource?: string;
        configColumnId?: string;
        metricKey?: string;
        pinGroupColIds?: string[];
        pinGroupLabel?: string;
      };
      columnGroupShow?: "open" | "closed";
    }

    interface BaseGroupColumnDef {
      groupId: string;
      headerName: string;
      marryChildren?: boolean;
      openByDefault?: boolean;
      children: BaseLeafColumnDef[];
    }

    const baseColumnDefs = buildSmartTableBaseColumnDefs<
      SmartColumn,
      BaseLeafColumnDef,
      BaseGroupColumnDef,
      { data?: AgGridRowData }
    >(this.sortedColumns, {
      createLeafColumnDef: ({
        column,
        colId,
        field,
        headerName,
        columnType,
        valueGetter,
        columnGroupShow,
        parentColumnId,
        compositionSource,
      }) => {
        const metricKey = typeof column.metric_key === "string" ? column.metric_key : undefined;
        const valueFormatter = getExecutionMetadataValueFormatter(metricKey);

        return {
          colId,
          field: field ?? colId,
          ...(valueGetter ? { valueGetter } : {}),
          ...(valueFormatter ? { valueFormatter } : {}),
          headerName,
          editable:
            isPlainTextEditableColumnType(columnType) && !isExecutionMetadataChildColumn(column),
          context: {
            columnType: String(metricKey ? ColumnType.TEXT : (columnType ?? ColumnType.TEXT)),
            ...(parentColumnId ? { parentColumnId } : {}),
            ...(compositionSource ? { compositionSource } : {}),
            ...(metricKey ? { metricKey } : {}),
          },
          ...(columnGroupShow ? { columnGroupShow } : {}),
        };
      },
      createGroupColumnDef: ({ groupId, headerName, children }) => ({
        groupId,
        headerName,
        marryChildren: true,
        openByDefault: true,
        children,
      }),
    });

    const nestedColumnDefs = nestSmartTableGroupedChildColumns({
      columns: this.sortedColumns,
      columnDefs: baseColumnDefs,
    });

    return nestedColumnDefs.map(columnDef => {
      if (isSmartTableColumnGroupDef(columnDef)) {
        const children = (columnDef.children ?? []).flatMap(child => {
          if (isSmartTableColumnGroupDef(child)) {
            return [];
          }
          const leaf = child as BaseLeafColumnDef;
          return [
            {
              colId: leaf.colId,
              field: leaf.field,
              ...(leaf.valueGetter ? { valueGetter: leaf.valueGetter } : {}),
              ...(leaf.valueFormatter ? { valueFormatter: leaf.valueFormatter } : {}),
              headerName: leaf.headerName,
              editable: leaf.editable,
              context: leaf.context,
              ...(leaf.columnGroupShow ? { columnGroupShow: leaf.columnGroupShow } : {}),
            },
          ];
        });

        return {
          groupId: columnDef.groupId ?? "",
          headerName: columnDef.headerName ?? "",
          marryChildren: columnDef.marryChildren,
          openByDefault: columnDef.openByDefault,
          children,
        };
      }

      const leaf = columnDef as BaseLeafColumnDef;
      return {
        colId: leaf.colId,
        field: leaf.field ?? leaf.colId,
        ...(leaf.valueFormatter ? { valueFormatter: leaf.valueFormatter } : {}),
        headerName: leaf.headerName,
        editable: leaf.editable,
        context: leaf.context,
      };
    });
  }

  /**
   * AG Grid row data derived from rows store.
   * Intended for initial render / hydration only.
   * Do not make this computed.struct: structural equality would compare every
   * row and full cell payload on each recompute if anything observes it.
   */
  get agGridRowData(): AgGridRowData[] {
    const sortedIndices = (Array.from(this.rows.keys()) as number[]).sort((a, b) => a - b);
    return sortedIndices.map(rowIndex => this._buildRowData(rowIndex));
  }

  get loadedRowIndices(): number[] {
    return (Array.from(this.rows.keys()) as number[]).sort((a, b) => a - b);
  }

  getPendingLoadedRowRanges = (maxRowsPerRange = 1_000): { startRow: number; endRow: number }[] => {
    const normalizedMaxRows = Math.max(1, Math.min(1_000, Math.trunc(maxRowsPerRange)));
    const pendingRowIndices: number[] = [];

    for (const [rowIndex, cellMap] of this.rows.entries()) {
      let hasPendingCell = false;
      for (const cell of (cellMap as ObservableMap<string, SmartCell>).values()) {
        if (PENDING_CELL_STATUSES.has(cell.status)) {
          hasPendingCell = true;
          break;
        }
      }
      if (hasPendingCell) {
        pendingRowIndices.push(rowIndex);
      }
    }

    pendingRowIndices.sort((left, right) => left - right);
    if (pendingRowIndices.length === 0) {
      return [];
    }

    const ranges: { startRow: number; endRow: number }[] = [];
    let startRow = pendingRowIndices[0];
    let previousRow = startRow;

    const pushChunkedRange = (rangeStart: number, rangeEndInclusive: number) => {
      for (let chunkStart = rangeStart; chunkStart <= rangeEndInclusive; ) {
        const chunkEnd = Math.min(rangeEndInclusive + 1, chunkStart + normalizedMaxRows);
        ranges.push({ startRow: chunkStart, endRow: chunkEnd });
        chunkStart = chunkEnd;
      }
    };

    for (const rowIndex of pendingRowIndices.slice(1)) {
      if (rowIndex === previousRow + 1) {
        previousRow = rowIndex;
        continue;
      }

      pushChunkedRange(startRow, previousRow);
      startRow = rowIndex;
      previousRow = rowIndex;
    }

    pushChunkedRange(startRow, previousRow);
    return ranges;
  };

  getAdjacentLoadedRowIndex(rowIndex: number, delta: number): number {
    const indices = this.loadedRowIndices;
    if (indices.length === 0) {
      return rowIndex;
    }

    const currentPosition = indices.indexOf(rowIndex);
    const basePosition = currentPosition >= 0 ? currentPosition : 0;
    const nextPosition = Math.max(0, Math.min(basePosition + delta, indices.length - 1));
    return indices[nextPosition];
  }

  /**
   * Count of cells currently in an execution-pending state.
   * Pending statuses include: QUEUED, DISPATCHED, RUNNING.
   */
  get executionPendingCellCount(): number {
    let count = 0;
    for (const cellMap of this.rows.values()) {
      for (const cell of (cellMap as ObservableMap<string, SmartCell>).values()) {
        if (PENDING_CELL_STATUSES.has(cell.status)) {
          count += 1;
        }
      }
    }
    return count;
  }

  /**
   * Whether this sheet has any cells currently executing (or queued to execute).
   * Useful for sheet-level running indicators in the tab navigator.
   */
  get isExecutionPending(): boolean {
    for (const cellMap of this.rows.values()) {
      for (const cell of (cellMap as ObservableMap<string, SmartCell>).values()) {
        if (PENDING_CELL_STATUSES.has(cell.status)) {
          return true;
        }
      }
    }
    return false;
  }

  /**
   * Count of cells with STALE status.
   */
  get staleCount(): number {
    let count = 0;
    for (const cellMap of this.rows.values()) {
      for (const [colId, cell] of (cellMap as ObservableMap<string, SmartCell>).entries()) {
        if (
          cell.status === CellStatus.STALE &&
          !isStalenessExemptColumn(this.columns.get(colId), this.columns)
        ) {
          count += 1;
        }
      }
    }
    return count;
  }

  /**
   * Sorted list of row indices that contain at least one STALE computed
   * (non-TEXT) cell. The backend `statuses: [STALE]` filter ensures only
   * STALE cells are queued, so COMPLETED cells in the same row are safe.
   */
  get staleRowIndices(): number[] {
    const computedColumnIds = new Set<string>();
    for (const [colId, col] of this.columns.entries()) {
      if (!isStalenessExemptColumn(col, this.columns)) {
        computedColumnIds.add(colId);
      }
    }

    if (computedColumnIds.size === 0) {
      return [];
    }

    const result: number[] = [];

    for (const [rowIndex, cellMap] of this.rows.entries()) {
      for (const colId of computedColumnIds) {
        const cell = (cellMap as ObservableMap<string, SmartCell>).get(colId);
        if (cell && cell.status === CellStatus.STALE) {
          result.push(rowIndex);
          break;
        }
      }
    }

    return result.sort((a, b) => a - b);
  }

  /**
   * Count of rows containing at least one stale computed cell.
   */
  get staleRowCount(): number {
    return this.staleRowIndices.length;
  }

  /**
   * Count of cells with FAILED status in computed (non-stable-display) columns.
   */
  get failedCount(): number {
    const computedColumnIds = new Set<string>();
    for (const [colId, col] of this.columns.entries()) {
      if (!isStalenessExemptColumn(col, this.columns)) {
        computedColumnIds.add(colId);
      }
    }

    if (computedColumnIds.size === 0) {
      return 0;
    }

    let count = 0;
    for (const cellMap of this.rows.values()) {
      for (const colId of computedColumnIds) {
        const cell = (cellMap as ObservableMap<string, SmartCell>).get(colId);
        if (cell?.status === CellStatus.FAILED) {
          count += 1;
        }
      }
    }

    return count;
  }

  /**
   * Sorted list of row indices that contain at least one FAILED computed cell.
   */
  get failedRowIndices(): number[] {
    const computedColumnIds = new Set<string>();
    for (const [colId, col] of this.columns.entries()) {
      if (!isStalenessExemptColumn(col, this.columns)) {
        computedColumnIds.add(colId);
      }
    }

    if (computedColumnIds.size === 0) {
      return [];
    }

    const result: number[] = [];

    for (const [rowIndex, cellMap] of this.rows.entries()) {
      for (const colId of computedColumnIds) {
        const cell = (cellMap as ObservableMap<string, SmartCell>).get(colId);
        if (cell && cell.status === CellStatus.FAILED) {
          result.push(rowIndex);
          break;
        }
      }
    }

    return result.sort((a, b) => a - b);
  }

  /**
   * Whether the sheet contains at least one computed (non-stable-display) column.
   */
  get hasComputedColumns(): boolean {
    for (const col of this.columns.values()) {
      if (!isStalenessExemptColumn(col, this.columns)) {
        return true;
      }
    }
    return false;
  }

  /**
   * Count of rows that contain at least one cell in a computed (non-TEXT)
   * column. Since every row shares the same column schema, this is simply
   * `rows.size` when computed columns exist, otherwise 0.
   */
  get totalComputedRowCount(): number {
    return this.hasComputedColumns ? this.rows.size : 0;
  }

  /**
   * All row indices as a sorted number array (used by the "re-run all" feature).
   */
  get allRowIndices(): number[] {
    return (Array.from(this.rows.keys()) as number[]).sort((a, b) => a - b);
  }

  /**
   * Sorted row indices that have at least one STALE computed cell.
   * This is the pending work set used by the batch runner.
   */
  get pendingRowIndices(): number[] {
    const computedColumnIds = new Set<string>();
    for (const [colId, col] of this.columns.entries()) {
      if (!isStalenessExemptColumn(col, this.columns)) {
        computedColumnIds.add(colId);
      }
    }
    if (computedColumnIds.size === 0) {
      return [];
    }

    const result: number[] = [];
    for (const [rowIndex, cellMap] of this.rows.entries()) {
      for (const colId of computedColumnIds) {
        const cell = (cellMap as ObservableMap<string, SmartCell>).get(colId);
        if (cell && cell.status === CellStatus.STALE) {
          result.push(rowIndex);
          break;
        }
      }
    }
    return result.sort((a, b) => a - b);
  }
}
